import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import axios from "axios";
import {
  Layout,
  Input,
  InputNumber,
  Button,
  Select,
  Card,
  Badge,
  Drawer,
  Empty,
  List,
  Spin,
  Modal,
  Form,
  Row,
  Col,
  message,
  Switch,
  Space,
  Tag,
  Tooltip,
  Typography,
  Alert,
  Collapse,
} from "antd";
import {
  BellOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SearchOutlined,
  QrcodeOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ShoppingCartOutlined,
  CloseOutlined,
  MenuOutlined,
  PrinterOutlined,
  PlusOutlined,
  MinusOutlined,
  RollbackOutlined,
  PercentageOutlined,
  LockOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  SendOutlined,
  UnorderedListOutlined,
  TeamOutlined,
  DollarOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import { Html5QrcodeScanner } from "html5-qrcode";
import { useSelection } from "../SelectionContext"; // Import useSelection hook
import "./GestionPos.css"; // Vos styles personnalisés
import TicketCaisse from "./TicketCaisse"; // Import du composant TicketCaisse
import VirtualizedProductGrid from "./VirtualizedProductGrid";
import { useAuth } from "../context/AuthContext"; // Use AuthContext
import API_CONFIG from '../config/api.js';
import { normalizeImageUrl, getImagePlaceholder } from '../utils/imageUrlHelper.js';
import QuickClientModal from "../gestion-vente/components/QuickClientModal";
import { fetchWithCache, buildCacheKey } from "../utils/offlineCache";
import { enqueueRequest, buildTempId, isOffline } from "../utils/offlineQueue";
import apiClient from "../api/apiClient";
import PosReprintCenter from "./PosReprintCenter";
import ServerManagerModal, { PosServer } from "./ServerManagerModal";
import GestionPosMobile from "./GestionPosMobile";
import { isCashMode as isMobileCashMode } from "./posMobileShared";
import { playBusinessAudioAlert } from "../utils/restaurantAudio";
import { getFrontOfHouseAction, getPreparationProgress } from "../utils/restaurantWorkflow";
import BusinessAudioToggle from "../components/BusinessAudioToggle";
import BusinessResourceNotice from "../components/BusinessResourceNotice";
import usePosCatalogOptions from "./usePosCatalogOptions";
import ManualInvoiceNumberField from "../components/invoices/ManualInvoiceNumberField";
import { useManualInvoiceNumber } from "../hooks/useInvoiceNumbering";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import StockShortageActionsModal from "../components/StockShortageActionsModal";
import {
  buildMissingStockItems,
  buildSalesProductionReplenishmentNavigation,
  buildSalesProductionReplenishmentRequest,
  buildStockReplenishmentRequest,
  splitStockShortageItems,
} from "../utils/stockReplenishment";
import {
  deleteSalesReplenishmentDraft,
  finalizeSalesReplenishmentDraft,
  loadSalesReplenishmentDraft,
  mergePosDraftCartWithShortage,
  saveSalesReplenishmentDraft,
} from "../utils/salesReplenishmentDraft";
import { getSelectedProductionTerminology } from "../GestionProductions/productionTerminology";
import {
  appendCashDigit,
  getCheckoutActionPresentation,
  getSuggestedCashAmount,
  normalizeCashAmount,
  removeLastCashDigit,
} from "./posCheckoutPresentation";

const { Header, Content, Footer } = Layout;
const { Option } = Select;
const { Text, Title } = Typography;
const API_URL = API_CONFIG.BASE_URL;
const PRODUCT_VIRTUALIZATION_THRESHOLD = 200;
const RESTAURANT_ORDER_POLL_INTERVAL_MS = 15000;
const PURCHASE_VIEW_PERMISSION =
  "Gestion Commerciale.Approvisionnement.Achats.Achat.view";
const PURCHASE_CREATE_PERMISSION =
  "Gestion Commerciale.Approvisionnement.Achats.Achat.create";
const STOCK_ADJUST_VIEW_PERMISSION =
  "Gestion Commerciale.Stock.GestionStock.Adjust.view";
const STOCK_ADJUST_CREATE_PERMISSION =
  "Gestion Commerciale.Stock.GestionStock.Adjust.create";
const PRODUCTION_MANAGE_VIEW_PERMISSION =
  "Gestion Commerciale.Approvisionnement.Production.Gerer.view";
const PRODUCTION_MANAGE_CREATE_PERMISSION =
  "Gestion Commerciale.Approvisionnement.Production.Gerer.create";
const ACTIVE_RESTAURANT_ORDER_STATUSES = new Set([
  "confirmed",
  "sent_to_kitchen",
  "in_preparation",
  "ready",
  "served",
]);

const RESTAURANT_FLOW_STATUS_META: Record<
  string,
  { label: string; color: string }
> = {
  confirmed: { label: "Nouvelle", color: "blue" },
  sent_to_kitchen: { label: "En cuisine", color: "gold" },
  in_preparation: { label: "En préparation", color: "orange" },
  ready: { label: "Prête à servir", color: "green" },
  served: { label: "Servie", color: "purple" },
  closed: { label: "Clôturée", color: "default" },
  cancelled: { label: "Annulée", color: "red" },
};

const RESTAURANT_SOURCE_META: Record<string, { label: string; color: string }> = {
  public_menu: { label: "Menu digital", color: "cyan" },
  staff: { label: "Prise de commande", color: "geekblue" },
};

const RESTAURANT_SERVICE_MODE_LABELS: Record<string, string> = {
  dine_in: "Sur place",
  takeaway: "À emporter",
  delivery: "Livraison",
};

interface PosCartProductThumbnailProps {
  imagePath?: string | null;
  productName: string;
}

const PosCartProductThumbnail: React.FC<PosCartProductThumbnailProps> = ({
  imagePath,
  productName,
}) => {
  const imageSource = useMemo(
    () =>
      imagePath?.trim() ? normalizeImageUrl(imagePath, API_URL) : "",
    [imagePath]
  );
  const [failedSource, setFailedSource] = useState<string | null>(null);
  const shouldShowFallback = !imageSource || failedSource === imageSource;

  if (shouldShowFallback) {
    return (
      <div
        className="pos-cart-item__image pos-cart-item__image--fallback"
        aria-label={`Aucune image disponible pour ${productName}`}
        role="img"
      >
        <PictureOutlined aria-hidden="true" />
      </div>
    );
  }

  return (
    <img
      className="pos-cart-item__image"
      src={imageSource}
      alt=""
      onError={() => setFailedSource(imageSource)}
    />
  );
};

// --- Définition des types ---

type StockManagementMode = "standard" | "pre_order" | "unlimited";

interface Product {
  id_produit: number;
  item_type?: "product" | "service";
  product_id?: number | null;
  service_id?: number | null;
  nom_produit: string;
  prix_vente: number;
  quantite_stock: number | null;
  stock_management_mode?: StockManagementMode;
  product_type?: string;
  production_kind?: "finished" | "semi_finished" | string | null;
  // Recette (production) vendable sans production prealable : autorise la vente
  // sans stock (stock negatif), regularise en fin de journee.
  sellable_without_production?: boolean;
  image: string;
  id_categorie: number;
  code_qr?: string;
  duration_minutes?: number | null;
}

interface CartItem extends Omit<Product, "quantite_stock"> {
  cartQuantity: number;
  prix_unitaire_HT?: number;
}

interface Customer {
  ID_Client: number;
  Nom_Raison_Sociale: string;
  phone?: string;
  email?: string;
  address?: string;
}

interface Tax {
  id: number;
  parent_id?: number;
  tax_type?: string;
  company_id?: number;
  code: string;
  name: string;
  status: string;
  rate: number;
  effective_date: string;
  created_at?: string;
  updated_at?: string;
  description?: string;
}

interface Category {
  id: number;
  categorie: string;
}

interface PosPaymentRoute {
  id?: number;
  payment_mode_id: number;
  treasury_account_id: number;
  payment_mode_name?: string;
  treasury_account_name?: string;
}

interface LoyaltyEligibility {
  eligible: boolean;
  reason?: string;
  discount_rate?: number;
  discount_amount?: number;
  prompt_on_sale?: boolean;
  orders_count?: number;
  orders_required?: number;
  remaining_orders?: number;
  periods_count?: number;
  periods_required?: number;
  remaining_periods?: number;
}

interface RestaurantOrderItem {
  product_id: number;
  product_name?: string;
  quantity?: number;
  subtotal?: number;
  unit_short_name?: string;
  kitchen_ticket_status?: string | null;
  line_status?: string;
}

interface RestaurantOrderSummary {
  id: number;
  order_number: string;
  order_date: string;
  flow_status: string;
  kitchen_status?: string;
  ready_items?: number;
  active_items?: number;
  source_channel?: string;
  service_mode?: string;
  table_label?: string | null;
  customer_name?: string | null;
  client_name?: string | null;
  total?: number;
  total_items?: number;
  total_quantity?: number;
  warehouse_id?: number;
  source_sales_order_id?: number | null;
  items?: RestaurantOrderItem[];
}

const getAuthHeaders = () => {
  const token = localStorage.getItem("authToken");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const formatRestaurantMoney = (value: any) =>
  Number(value || 0).toLocaleString("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

const formatRestaurantDate = (value?: string) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getRestaurantStatusMeta = (status?: string) =>
  RESTAURANT_FLOW_STATUS_META[String(status || "").toLowerCase()] || {
    label: status || "Non défini",
    color: "default",
  };

const getRestaurantSourceMeta = (source?: string) =>
  RESTAURANT_SOURCE_META[String(source || "").toLowerCase()] || {
    label: "Restaurant",
    color: "default",
  };

const getRestaurantServiceModeLabel = (serviceMode?: string) =>
  RESTAURANT_SERVICE_MODE_LABELS[String(serviceMode || "").toLowerCase()] ||
  serviceMode ||
  "Service restaurant";

const isActiveRestaurantOrder = (order: RestaurantOrderSummary) =>
  ACTIVE_RESTAURANT_ORDER_STATUSES.has(
    String(order.flow_status || "").toLowerCase()
  );

const needsRestaurantHandling = (order: RestaurantOrderSummary) =>
  String(order.flow_status || "").toLowerCase() === "confirmed";

type RestaurantOrderAction = "kitchen" | "served" | "closed";

interface RestaurantOrdersPosPanelProps {
  open: boolean;
  onClose: () => void;
  orders: RestaurantOrderSummary[];
  loading: boolean;
  error: string;
  selectedOrderId: number | null;
  onSelectOrder: (orderId: number) => void;
  onRefresh: () => void;
  onAction: (
    order: RestaurantOrderSummary,
    nextAction: RestaurantOrderAction
  ) => Promise<void> | void;
  onConvert: (order: RestaurantOrderSummary) => void;
  convertDisabled?: boolean;
  actionLoading: string | null;
  isMobile: boolean;
}

const getRestaurantOrderItemCount = (order?: RestaurantOrderSummary | null) => {
  if (!order) return 0;
  const totalItems = Number(order.total_items || 0);
  if (totalItems > 0) return totalItems;
  const totalQuantity = Number(order.total_quantity || 0);
  if (totalQuantity > 0) return totalQuantity;
  return (order.items || []).reduce(
    (sum, item) => sum + Number(item.quantity || 0),
    0
  );
};

const getRestaurantOrderAction = (order?: RestaurantOrderSummary | null) => {
  const status = String(order?.flow_status || "").toLowerCase();
  if (getFrontOfHouseAction(status) === "kitchen") {
    return {
      key: "kitchen" as RestaurantOrderAction,
      label: "Prendre en charge",
      icon: <SendOutlined />,
    };
  }
  if (getFrontOfHouseAction(status) === "served") {
    return {
      key: "served" as RestaurantOrderAction,
      label: "Marquer servie",
      icon: <CheckCircleOutlined />,
    };
  }
  if (getFrontOfHouseAction(status) === "closed") {
    return {
      key: "closed" as RestaurantOrderAction,
      label: "Clôturer",
      icon: <CheckCircleOutlined />,
    };
  }
  return null;
};

const RestaurantOrdersPosPanel: React.FC<RestaurantOrdersPosPanelProps> = ({
  open,
  onClose,
  orders,
  loading,
  error,
  selectedOrderId,
  onSelectOrder,
  onRefresh,
  onAction,
  onConvert,
  convertDisabled,
  actionLoading,
  isMobile,
}) => {
  const selectedOrder = useMemo(() => {
    if (!orders.length) return null;
    return (
      orders.find((order) => order.id === selectedOrderId) || orders[0] || null
    );
  }, [orders, selectedOrderId]);

  const pendingCount = useMemo(
    () => orders.filter(needsRestaurantHandling).length,
    [orders]
  );

  const activeTotal = useMemo(
    () => orders.reduce((sum, order) => sum + Number(order.total || 0), 0),
    [orders]
  );

  const selectedStatusMeta = getRestaurantStatusMeta(
    selectedOrder?.flow_status
  );
  const selectedSourceMeta = getRestaurantSourceMeta(
    selectedOrder?.source_channel
  );
  const selectedAction = getRestaurantOrderAction(selectedOrder);

  return (
    <Drawer
      className="pos-restaurant-drawer"
      title={
        <Space size={10} wrap>
          <BellOutlined />
          <span>Commandes restaurant</span>
          <Tag color="blue">{orders.length} en cours</Tag>
        </Space>
      }
      extra={
        <Button
          icon={<ReloadOutlined />}
          loading={loading}
          onClick={onRefresh}
          size={isMobile ? "small" : "middle"}
        >
          Actualiser
        </Button>
      }
      width={isMobile ? "100%" : 760}
      placement="right"
      open={open}
      onClose={onClose}
      destroyOnClose={false}
    >
      <div className="pos-restaurant-panel">
        <aside className="pos-restaurant-panel__list">
          <div className="pos-restaurant-panel__stats">
            <div>
              <span>Actives</span>
              <strong>{orders.length}</strong>
            </div>
            <div>
              <span>À prendre</span>
              <strong>{pendingCount}</strong>
            </div>
            <div>
              <span>Total</span>
              <strong>{formatRestaurantMoney(activeTotal)} CFA</strong>
            </div>
          </div>

          {error && <div className="pos-restaurant-panel__error">{error}</div>}

          {loading && orders.length === 0 ? (
            <div className="pos-restaurant-panel__loading">
              <Spin />
              <Text type="secondary">Chargement des commandes...</Text>
            </div>
          ) : orders.length === 0 ? (
            <Empty
              className="pos-restaurant-panel__empty"
              description="Aucune commande active"
            />
          ) : (
            <List
              className="pos-restaurant-panel__orders"
              dataSource={orders}
              renderItem={(order) => {
                const statusMeta = getRestaurantStatusMeta(order.flow_status);
                const sourceMeta = getRestaurantSourceMeta(
                  order.source_channel
                );
                const selected = selectedOrder?.id === order.id;
                return (
                  <List.Item>
                    <button
                      type="button"
                      className={`pos-restaurant-order-card${
                        selected ? " is-selected" : ""
                      }${needsRestaurantHandling(order) ? " needs-action" : ""}`}
                      onClick={() => onSelectOrder(order.id)}
                    >
                      <div className="pos-restaurant-order-card__top">
                        <strong>{order.order_number}</strong>
                        {selected && <EyeOutlined />}
                      </div>
                      <div className="pos-restaurant-order-card__meta">
                        <span>
                          <ClockCircleOutlined />{" "}
                          {formatRestaurantDate(order.order_date)}
                        </span>
                        <Tag color={sourceMeta.color}>{sourceMeta.label}</Tag>
                      </div>
                      <div className="pos-restaurant-order-card__tags">
                        <Tag color={statusMeta.color}>{statusMeta.label}</Tag>
                        {getPreparationProgress(order) && <span>{getPreparationProgress(order)}</span>}
                        <Tag>
                          {order.table_label ||
                            getRestaurantServiceModeLabel(order.service_mode)}
                        </Tag>
                        {needsRestaurantHandling(order) && (
                          <Tag color="red">Non prise en charge</Tag>
                        )}
                      </div>
                      <div className="pos-restaurant-order-card__foot">
                        <span>{getRestaurantOrderItemCount(order)} article(s)</span>
                        <strong>
                          {formatRestaurantMoney(order.total)} CFA
                        </strong>
                      </div>
                    </button>
                  </List.Item>
                );
              }}
            />
          )}
        </aside>

        <section className="pos-restaurant-panel__detail">
          {selectedOrder ? (
            <div className="pos-restaurant-detail-card">
              <div className="pos-restaurant-detail-card__header">
                <div>
                  <Text type="secondary">Commande sélectionnée</Text>
                  <Title level={4}>{selectedOrder.order_number}</Title>
                </div>
                <Tag color={selectedStatusMeta.color}>
                  {selectedStatusMeta.label}
                </Tag>
                {getPreparationProgress(selectedOrder) && <Text>{getPreparationProgress(selectedOrder)}</Text>}
              </div>

              <div className="pos-restaurant-detail-card__badges">
                <Tag color={selectedSourceMeta.color}>
                  {selectedSourceMeta.label}
                </Tag>
                <Tag>{selectedOrder.table_label || "Sans table"}</Tag>
                <Tag>
                  {getRestaurantServiceModeLabel(selectedOrder.service_mode)}
                </Tag>
              </div>

              <div className="pos-restaurant-detail-card__summary">
                <div>
                  <span>Total</span>
                  <strong>
                    {formatRestaurantMoney(selectedOrder.total)} CFA
                  </strong>
                </div>
                <div>
                  <span>Articles</span>
                  <strong>{getRestaurantOrderItemCount(selectedOrder)}</strong>
                </div>
                <div>
                  <span>Client</span>
                  <strong>
                    {selectedOrder.customer_name ||
                      selectedOrder.client_name ||
                      "Client comptoir"}
                  </strong>
                </div>
              </div>

              <div className="pos-restaurant-detail-card__section">
                <Text strong>Contenu de la commande</Text>
                <div className="pos-restaurant-detail-card__items">
                  {(selectedOrder.items || []).length > 0 ? (
                    selectedOrder.items?.map((item, index) => (
                      <div
                        className="pos-restaurant-detail-card__item"
                        key={`${selectedOrder.id}-${item.product_id}-${index}`}
                      >
                        <div>
                          <strong>{item.product_name || "Article"}</strong>
                          <span>
                            {Number(item.quantity || 0).toLocaleString(
                              "fr-FR"
                            )}{" "}
                            {item.unit_short_name || "u"}
                          </span>
                        </div>
                        <strong>
                          {formatRestaurantMoney(item.subtotal)} CFA
                        </strong>
                      </div>
                    ))
                  ) : (
                    <Text type="secondary">
                      Les lignes de cette commande ne sont pas disponibles.
                    </Text>
                  )}
                </div>
              </div>

              <div className="pos-restaurant-detail-card__actions">
                {selectedAction ? (
                  <Button
                    icon={selectedAction.icon}
                    loading={
                      actionLoading ===
                      `${selectedAction.key}:${selectedOrder.id}`
                    }
                    onClick={() =>
                      onAction(selectedOrder, selectedAction.key)
                    }
                    block={isMobile}
                  >
                    {selectedAction.label}
                  </Button>
                ) : null}
                {!selectedOrder.source_sales_order_id &&
                Number(selectedOrder.total) > 0 ? (
                  <Button
                    type="primary"
                    icon={<DollarOutlined />}
                    onClick={() => onConvert(selectedOrder)}
                    disabled={convertDisabled}
                    block={isMobile}
                  >
                    Encaisser / Convertir en vente
                  </Button>
                ) : null}
                {!selectedAction &&
                (selectedOrder.source_sales_order_id ||
                  !(Number(selectedOrder.total) > 0)) ? (
                  <Tag color="default">
                    {selectedOrder.source_sales_order_id
                      ? "Commande déjà encaissée"
                      : "Aucune action disponible"}
                  </Tag>
                ) : null}
              </div>
            </div>
          ) : (
            <Empty
              className="pos-restaurant-panel__empty"
              description="Sélectionnez une commande"
            />
          )}
        </section>
      </div>
    </Drawer>
  );
};

const resolvePreferredPosPaymentMode = (
  modes: Array<{ id: number | string }> = [],
  routes: PosPaymentRoute[] = []
): number | null => {
  if (!Array.isArray(modes) || modes.length === 0) {
    return null;
  }

  const availableModeIds = new Set(
    modes
      .map((mode) => Number(mode?.id))
      .filter((modeId) => Number.isInteger(modeId) && modeId > 0)
  );

  const routedModeId = routes
    .map((route) => Number(route?.payment_mode_id))
    .find((modeId) => availableModeIds.has(modeId));

  if (routedModeId) {
    return routedModeId;
  }

  const firstModeId = Number(modes[0]?.id);
  return Number.isInteger(firstModeId) && firstModeId > 0 ? firstModeId : null;
};

const normalizeStockManagementMode = (
  mode: unknown
): StockManagementMode => {
  const normalized = String(mode || "standard")
    .trim()
    .toLowerCase();
  if (normalized === "pre_order" || normalized === "unlimited") {
    return normalized;
  }
  return "standard";
};

const requiresStrictStockCheck = (product: {
  stock_management_mode?: unknown;
  sellable_without_production?: unknown;
}): boolean =>
  normalizeStockManagementMode(product?.stock_management_mode) === "standard" &&
  !product?.sellable_without_production;

const getStockLabel = (product: Product): string => {
  // Recette vendable sans production : la vente est autorisee sans stock
  // (stock negatif assume, regularise en fin de journee) => libelle clair,
  // pas un signal de rupture/indisponibilite.
  if (product.sellable_without_production) {
    return `Disponible (vente sans production, stock: ${product.quantite_stock})`;
  }
  const mode = normalizeStockManagementMode(product.stock_management_mode);
  if (mode === "unlimited") {
    return "Disponibilite: illimitee";
  }
  if (mode === "pre_order") {
    return `Disponibilite: sur commande (stock actuel: ${product.quantite_stock})`;
  }
  return product.quantite_stock == null ? "Stock à vérifier" : `Stock: ${product.quantite_stock}`;
};

// Affichage compact du stock sur les cartes produit. Pour une recette vendable
// sans production, on remplace le long libelle par une etiquette discrete dont
// l'info complete s'affiche au survol (gain de place sur la carte).
const renderStockInfo = (product: Product) => {
  if (product.sellable_without_production) {
    return (
      <Tooltip title={getStockLabel(product)}>
        <Tag color="gold" style={{ margin: 0, cursor: "help" }}>
          Sans prod.
        </Tag>
      </Tooltip>
    );
  }
  return <>{getStockLabel(product)}</>;
};

// --- Composant principal ---
const GestionPos: React.FC = () => {
  const outletContext = useOutletContext<{ openMobileMenu?: () => void }>();
  const navigate = useNavigate();
  const location = useLocation();
  // Utiliser le contexte de sélection pour accéder au magasin et à l'entreprise sélectionnés
  const {
    selectedWarehouse,
    setSelectedWarehouse,
    selectedCompany,
    setSelectedCompany,
  } = useSelection();
  const { user, hasPermission } = useAuth();
  const canCreateClient =
    user?.is_superadmin === true ||
    Number(user?.is_superadmin) === 1 ||
    hasPermission("Gestion Commerciale.Entites.Clients.create");
  const canCreatePurchase =
    hasPermission(PURCHASE_VIEW_PERMISSION) &&
    hasPermission(PURCHASE_CREATE_PERMISSION);
  const canCreateStockAdjustment =
    hasPermission(STOCK_ADJUST_VIEW_PERMISSION) &&
    hasPermission(STOCK_ADJUST_CREATE_PERMISSION);
  const canCreateProduction =
    hasPermission(PRODUCTION_MANAGE_VIEW_PERMISSION) &&
    hasPermission(PRODUCTION_MANAGE_CREATE_PERMISSION);
  const canUsePos = hasPermission("Gestion Commerciale.POS.use");

  const normalizedWarehouseId = useMemo(
    () => Number(selectedWarehouse?.id ?? selectedWarehouse ?? 0) || null,
    [selectedWarehouse]
  );

  const normalizedCompanyId = useMemo(
    () => Number(selectedCompany?.id ?? selectedCompany ?? 0) || null,
    [selectedCompany]
  );
  const productionTerminology = useMemo(
    () => getSelectedProductionTerminology(user, normalizedCompanyId),
    [normalizedCompanyId, user]
  );

  const manualInvoiceNumber = useManualInvoiceNumber({ companyId: normalizedCompanyId,
    warehouseId: normalizedWarehouseId, active: canUsePos });


  // Référence pour suivre la valeur précédente du magasin sélectionné
  const prevWarehouseRef = useRef<number | null>(null);
  const prevCompanyRef = useRef<number | null>(null);
  const suppressSelectionRollbackPromptRef = useRef(false);

  // États principaux
  const [taxes, setTaxes] = useState<Tax[]>([]);
  const [filteredTaxes, setFilteredTaxes] = useState<Tax[]>([]);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [stockShortageItems, setStockShortageItems] = useState<any[]>([]);

  // Ajout des modes de paiement depuis l'API
  const [paymentModes, setPaymentModes] = useState<any[]>([]);
  const [loadingPaymentModes, setLoadingPaymentModes] =
    useState<boolean>(false);
  const [posPaymentRoutes, setPosPaymentRoutes] = useState<PosPaymentRoute[]>(
    []
  );
  const [loadingPosPaymentRoutes, setLoadingPosPaymentRoutes] =
    useState<boolean>(false);
  const [posPaymentRoutesError, setPosPaymentRoutesError] =
    useState<string>("");

  const [selectedCustomer, setSelectedCustomer] = useState<number | null>(null);
  const [quickClientModalOpen, setQuickClientModalOpen] = useState<boolean>(false);
  const [selectedTax, setSelectedTax] = useState<Tax | null>(null);

  const [discount, setDiscount] = useState<number>(0);
  const [discountType, setDiscountType] = useState<'amount' | 'percentage'>('amount');
  const [discountPercentage, setDiscountPercentage] = useState<number>(0);
  const [activeCategory, setActiveCategory] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [scannedBarcode, setScannedBarcode] = useState<string>("");
  const [isScanning, setIsScanning] = useState<boolean>(false);
  const [scanner, setScanner] = useState<Html5QrcodeScanner | null>(null);
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [showSearchResults, setShowSearchResults] = useState<boolean>(false);
  const [editingItem, setEditingItem] = useState<number | null>(null);
  const [editPrice, setEditPrice] = useState<number>(0);
  const [showCart, setShowCart] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 1024);
  const [isPosMobile, setIsPosMobile] = useState<boolean>(window.innerWidth <= 768);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [restoredDraftMeta, setRestoredDraftMeta] = useState<{
    draftId: string;
    missingItemCount: number;
    priceChangedCount: number;
  } | null>(null);
  const restoredDraftHandledRef = useRef<string | null>(null);

  // États pour le ticket et le paiement
  const [ticketVisible, setTicketVisible] = useState<boolean>(false);
  const [orderData, setOrderData] = useState<any>(null);
  const [resetCartAfterTicketClose, setResetCartAfterTicketClose] =
    useState<boolean>(false);
  const [paidAmount, setPaidAmount] = useState<number>(0);
  const [amountReceived, setAmountReceived] = useState<number>(0);

  // Sélection du mode de paiement (maintenant par ID et non par index)
  const [selectedModePaiement, setSelectedModePaiement] = useState<
    number | null
  >(null);

  const [printReceipt, setPrintReceipt] = useState<boolean>(true);
  const [warehouseData, setWarehouseData] = useState<any>(null);
  const [reprintCenterOpen, setReprintCenterOpen] = useState<boolean>(false);
  const [restaurantOrdersPanelOpen, setRestaurantOrdersPanelOpen] =
    useState<boolean>(false);
  const [restaurantOrders, setRestaurantOrders] = useState<
    RestaurantOrderSummary[]
  >([]);
  const [restaurantOrdersLoading, setRestaurantOrdersLoading] =
    useState<boolean>(false);
  const [restaurantOrdersError, setRestaurantOrdersError] =
    useState<string>("");
  const [restaurantActionLoading, setRestaurantActionLoading] =
    useState<string | null>(null);
  const [selectedRestaurantOrderId, setSelectedRestaurantOrderId] =
    useState<number | null>(null);
  const [seenRestaurantOrderIds, setSeenRestaurantOrderIds] = useState<
    string[]
  >([]);

  // Encaissement / conversion d'une commande restaurant en vente POS.
  const [convertOrder, setConvertOrder] =
    useState<RestaurantOrderSummary | null>(null);
  const [convertSubmitting, setConvertSubmitting] = useState<boolean>(false);
  const [convertPayModeId, setConvertPayModeId] = useState<number | null>(null);
  const [convertPayAmount, setConvertPayAmount] = useState<number>(0);

  // Serveurs/serveuses (optionnel, profils restauration uniquement)
  // `serversFeatureEnabled` est pilote par le backend (profil d'activite de l'entreprise).
  const [serversFeatureEnabled, setServersFeatureEnabled] =
    useState<boolean>(false);
  const [posServers, setPosServers] = useState<PosServer[]>([]);
  const [selectedServerId, setSelectedServerId] = useState<number | null>(null);
  const [serverManagerOpen, setServerManagerOpen] = useState<boolean>(false);

  // États pour la recherche et le formulaire client
  const [showClientForm, setShowClientForm] = useState<boolean>(false);

  const returningDraftId = location.state?.stockReplenishmentReturn?.source_type === "pos"
    ? location.state.stockReplenishmentReturn.sales_draft_id : null;
  const returningDraft = useMemo(() => returningDraftId ? loadSalesReplenishmentDraft(returningDraftId) : null,
    [returningDraftId]);
  const returningDraftMatches = returningDraft?.source_type === "pos" &&
    Number(returningDraft.user_id) === Number(user?.id) &&
    Number(returningDraft.company_id) === normalizedCompanyId &&
    Number(returningDraft.warehouse_id) === normalizedWarehouseId;
  const catalogChoices = usePosCatalogOptions({
    enabled: canUsePos && Boolean(user?.id && normalizedCompanyId && normalizedWarehouseId),
    customerId: selectedCustomer || (returningDraftMatches ? Number(returningDraft?.payload?.selected_customer_id) || null : null),
    items: cart.length ? cart : returningDraftMatches && Array.isArray(returningDraft?.payload?.cart) ? returningDraft.payload.cart : [],
    categoryId: activeCategory, search: searchTerm, barcode: scannedBarcode,
  });
  const products: Product[] = catalogChoices.products;
  const categories: Category[] = catalogChoices.categoryChoices;
  const customers: Customer[] = catalogChoices.customers;
  const catalogLoaded = Boolean(catalogChoices.catalog.data) && catalogChoices.selectionReady;
  const currentPosContext = useRef(catalogChoices.contextKey);
  currentPosContext.current = catalogChoices.contextKey;
  useEffect(() => {
    currentPosContext.current = catalogChoices.contextKey;
    return () => { if (currentPosContext.current === catalogChoices.contextKey) currentPosContext.current = ""; };
  }, [catalogChoices.contextKey]);
  const cartContext = useRef(catalogChoices.contextKey);
  const cartContextMatches = cart.length === 0 || cartContext.current === catalogChoices.contextKey;
  const submitInProgress = useRef(false);
  const currentDraftKey = JSON.stringify([cart, selectedCustomer, discount, discountType, discountPercentage,
    selectedTax?.id, selectedModePaiement, paidAmount, amountReceived, selectedServerId, editingItem, editPrice, printReceipt]);
  const currentDraft = useRef(currentDraftKey);
  currentDraft.current = currentDraftKey;

  const handleQuickClientSuccess = useCallback(async (newClient: any) => {
    try {
      catalogChoices.assertContext();
      // The callback belongs to the context in which QuickClientModal was opened.
      void catalogChoices.invalidate("customer");
      setSelectedCustomer(Number(newClient.id));
      catalogChoices.setCustomerSearch("");
      setQuickClientModalOpen(false);
    } catch {
      message.info("Le client a été créé dans le contexte précédent. Rouvrez les choix dans l’établissement actuel.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogChoices.contextKey]);

  // Liste des modes de paiement disponibles (utilisée uniquement comme fallback)
  const modesPaiementDefault = [
    { id: 1, name: "Espèces" },
    { id: 2, name: "Carte bancaire" },
    { id: 3, name: "Virement" },
    { id: 4, name: "Chèque" },
    { id: 5, name: "Mobile Money" },
  ];

  // Mise à jour de l'état selon la taille de l'écran
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
      setIsPosMobile(window.innerWidth <= 768);
      if (window.innerWidth >= 1024) {
        setShowCart(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const restaurantSeenStorageKey = useMemo(
    () =>
      `pos_restaurant_seen_${normalizedCompanyId || "company"}_${
        normalizedWarehouseId || "warehouse"
      }`,
    [normalizedCompanyId, normalizedWarehouseId]
  );

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(restaurantSeenStorageKey);
      const parsed = stored ? JSON.parse(stored) : [];
      setSeenRestaurantOrderIds(Array.isArray(parsed) ? parsed.map(String) : []);
    } catch (error) {
      setSeenRestaurantOrderIds([]);
    }
  }, [restaurantSeenStorageKey]);

  const activeRestaurantOrders = useMemo(
    () => restaurantOrders.filter(isActiveRestaurantOrder),
    [restaurantOrders]
  );

  const restaurantAttentionCount = useMemo(() => {
    const seenIds = new Set(seenRestaurantOrderIds);
    return activeRestaurantOrders.filter(
      (order) =>
        needsRestaurantHandling(order) || !seenIds.has(String(order.id))
    ).length;
  }, [activeRestaurantOrders, seenRestaurantOrderIds]);

  const rememberRestaurantOrdersSeen = useCallback(
    (ordersToMark: RestaurantOrderSummary[]) => {
      setSeenRestaurantOrderIds((current) => {
        const nextSet = new Set(current.map(String));
        ordersToMark.forEach((order) => {
          if (order?.id) {
            nextSet.add(String(order.id));
          }
        });
        const next = Array.from(nextSet);
        if (
          next.length === current.length &&
          next.every((id) => current.includes(id))
        ) {
          return current;
        }
        try {
          window.localStorage.setItem(
            restaurantSeenStorageKey,
            JSON.stringify(next)
          );
        } catch (error) {
          // Le compteur reste fonctionnel même si le stockage local est indisponible.
        }
        return next;
      });
    },
    [restaurantSeenStorageKey]
  );

  const fetchRestaurantOrders = useCallback(
    async ({ silent = false }: { silent?: boolean } = {}) => {
      if (!normalizedCompanyId || !normalizedWarehouseId) {
        setRestaurantOrders([]);
        setSelectedRestaurantOrderId(null);
        setRestaurantOrdersError("");
        return;
      }

      try {
        if (!silent) {
          setRestaurantOrdersLoading(true);
        }
        setRestaurantOrdersError("");

        const response = await axios.get(API_CONFIG.apiUrl("/restaurant/orders"), {
          params: {
            company_id: normalizedCompanyId,
            warehouse_id: normalizedWarehouseId,
            page: 1,
            limit: 80,
          },
          headers: getAuthHeaders(),
        });

        const rows = Array.isArray(response.data?.orders)
          ? response.data.orders
          : [];
        const activeRows = rows.filter(isActiveRestaurantOrder);
        setRestaurantOrders(activeRows);
        setSelectedRestaurantOrderId((current) => {
          if (
            current &&
            activeRows.some((order: RestaurantOrderSummary) => order.id === current)
          ) {
            return current;
          }
          return activeRows[0]?.id ?? null;
        });
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.details ||
          error?.response?.data?.error ||
          error?.message ||
          "Impossible de charger les commandes restaurant.";
        setRestaurantOrdersError(errorMessage);
        setRestaurantOrders([]);
        setSelectedRestaurantOrderId(null);
      } finally {
        if (!silent) {
          setRestaurantOrdersLoading(false);
        }
      }
    },
    [normalizedCompanyId, normalizedWarehouseId]
  );

  useEffect(() => {
    void fetchRestaurantOrders({ silent: true });
    const interval = window.setInterval(() => {
      void fetchRestaurantOrders({ silent: true });
    }, RESTAURANT_ORDER_POLL_INTERVAL_MS);

    return () => window.clearInterval(interval);
  }, [fetchRestaurantOrders]);

  useEffect(() => {
    if (restaurantOrdersPanelOpen) {
      void fetchRestaurantOrders({ silent: false });
    }
  }, [fetchRestaurantOrders, restaurantOrdersPanelOpen]);

  // Charge la liste des serveurs (et le flag d'eligibilite renvoye par le backend).
  const fetchPosServers = useCallback(async () => {
    if (!normalizedCompanyId) {
      setServersFeatureEnabled(false);
      setPosServers([]);
      setSelectedServerId(null);
      return;
    }
    try {
      const response = await axios.get(API_CONFIG.apiUrl("/pos/servers"), {
        params: {
          company_id: normalizedCompanyId,
          warehouse_id: normalizedWarehouseId || undefined,
        },
        headers: getAuthHeaders(),
      });
      const enabled = Boolean(response.data?.feature_enabled);
      const list: PosServer[] = Array.isArray(response.data?.servers)
        ? response.data.servers
        : [];
      setServersFeatureEnabled(enabled);
      setPosServers(list);
      // Si le serveur selectionne n'est plus actif/present, on le deselectionne.
      setSelectedServerId((current) =>
        current && list.some((s) => s.id === current) ? current : null
      );
    } catch (error) {
      // En cas d'erreur on masque simplement la fonctionnalite (non bloquant).
      setServersFeatureEnabled(false);
      setPosServers([]);
      setSelectedServerId(null);
    }
  }, [normalizedCompanyId, normalizedWarehouseId]);

  useEffect(() => {
    void fetchPosServers();
  }, [fetchPosServers]);

  useEffect(() => {
    if (restaurantOrdersPanelOpen) {
      rememberRestaurantOrdersSeen(activeRestaurantOrders);
    }
  }, [
    activeRestaurantOrders,
    rememberRestaurantOrdersSeen,
    restaurantOrdersPanelOpen,
  ]);

  const handleOpenRestaurantOrdersPanel = () => {
    setRestaurantOrdersPanelOpen(true);
    rememberRestaurantOrdersSeen(activeRestaurantOrders);
  };

  const handleRestaurantOrderAction = useCallback(
    async (
      order: RestaurantOrderSummary,
      nextAction: RestaurantOrderAction
    ) => {
      if (!order?.id || !normalizedCompanyId) return;
      setRestaurantActionLoading(`${nextAction}:${order.id}`);
      try {
        if (nextAction === "kitchen") {
          await axios.post(
            API_CONFIG.apiUrl(`/restaurant/orders/${order.id}/send-to-kitchen`),
            { company_id: normalizedCompanyId },
            { headers: getAuthHeaders() }
          );
          message.success("Commande envoyée en cuisine.");
        } else {
          const nextStatus = nextAction;
          await axios.patch(
            API_CONFIG.apiUrl(`/restaurant/orders/${order.id}/status`),
            { company_id: normalizedCompanyId, flow_status: nextStatus },
            { headers: getAuthHeaders() }
          );
          message.success("Statut de la commande mis à jour.");
        }
        await fetchRestaurantOrders({ silent: true });
      } catch (error: any) {
        message.error(
          error?.response?.data?.details ||
            error?.response?.data?.error ||
            error?.message ||
            "Action impossible sur cette commande."
        );
      } finally {
        setRestaurantActionLoading(null);
      }
    },
    [fetchRestaurantOrders, normalizedCompanyId]
  );

  // Nettoyage du scanner lors du démontage
  useEffect(() => {
    return () => {
      if (scanner) {
        scanner.clear();
      }
    };
  }, [scanner]);

  // Filtrer les taxes en fonction de l'entreprise sélectionnée
  useEffect(() => {
    if (normalizedCompanyId && taxes.length > 0) {
      const filtered = taxes.filter(
        (tax) =>
          !tax.company_id || Number(tax.company_id) === normalizedCompanyId
      );
      setFilteredTaxes(filtered);

      // Réinitialiser la taxe sélectionnée si elle n'est plus dans la liste filtrée
      if (selectedTax && !filtered.find((t) => t.id === selectedTax.id)) {
        setSelectedTax(null);
      }
    } else {
      setFilteredTaxes(taxes);
    }
  }, [normalizedCompanyId, taxes, selectedTax]);

  const loadWarehouseData = async (warehouseId: number | null) => {
    if (!warehouseId) return null;
    const cacheKey = buildCacheKey("pos:warehouse", { id: warehouseId });
    return fetchWithCache(
      cacheKey,
      async () => {
        const response = await axios.get(
          API_CONFIG.apiUrl(`/warehouses/${warehouseId}`)
        );
        return response.data;
      },
      null
    );
  };

  const fetchPosPaymentRoutes = useCallback(
    async (warehouseId: number | null) => {
      const requestContext = currentPosContext.current;
      if (!warehouseId) {
        setPosPaymentRoutes([]);
        setPosPaymentRoutesError("");
        return [];
      }

      try {
        setLoadingPosPaymentRoutes(true);
        const cacheKey = buildCacheKey("pos:payment-routes", { warehouseId });
        const routes = await fetchWithCache(
          cacheKey,
          async () => {
            const response = await apiClient.get(
              `/api/warehouses/${warehouseId}/pos-payment-routes`
            );
            return Array.isArray(response.data?.data) ? response.data.data : [];
          },
          []
        );

        const normalizedRoutes = Array.isArray(routes) ? routes : [];
        if (currentPosContext.current !== requestContext) return [];
        setPosPaymentRoutes(normalizedRoutes);
        setPosPaymentRoutesError("");
        return normalizedRoutes;
      } catch (error) {
        if (currentPosContext.current !== requestContext) return [];
        setPosPaymentRoutes([]);
        setPosPaymentRoutesError(
          axios.isAxiosError(error)
            ? error.response?.data?.error ||
                "Impossible de charger le routage POS pour cet établissement."
            : "Impossible de charger le routage POS pour cet établissement."
        );
        return [];
      } finally {
        if (currentPosContext.current === requestContext) setLoadingPosPaymentRoutes(false);
      }
    },
    []
  );

  const fetchLoyaltyEligibility = useCallback(
    async (
      warehouseId: number,
      userId: number,
      amountHt: number
    ): Promise<LoyaltyEligibility | null> => {
      try {
        const response = await axios.get(
          API_CONFIG.apiUrl("/orders/loyalty-eligibility"),
          {
            params: {
              warehouse_id: warehouseId,
              user_id: userId,
              amount_ht: amountHt,
              company_id: normalizedCompanyId,
            },
          }
        );
        return response.data as LoyaltyEligibility;
      } catch (error) {
        return null;
      }
    },
    [normalizedCompanyId]
  );

  // Fonction pour charger les modes de paiement
  const fetchPaymentModes = async ({
    companyId = normalizedCompanyId,
    warehouseId = normalizedWarehouseId,
  }: {
    companyId?: number | null;
    warehouseId?: number | null;
  } = {}) => {
    const requestContext = currentPosContext.current;
    try {
      setLoadingPaymentModes(true);

      if (!companyId) {
        setWarehouseData(null);
        setPaymentModes([]);
        return [];
      }

      // Pr?paration des param?tres (filtrer par magasin/entreprise si disponible)
      const params: any = {};
      params.page = 1;
      params.limit = 1000;
      params.company_id = companyId;

      if (warehouseId) {
        try {
          // R?cup?rer les d?tails du magasin pour obtenir l'ID de l'entreprise
          const warehouse = await loadWarehouseData(warehouseId);
          if (currentPosContext.current !== requestContext) return [];
          if (warehouse) {
            setWarehouseData(warehouse);
            if (warehouse.company_id) {
              params.company_id = Number(warehouse.company_id);
            }
          }
        } catch (error) {
        }
      } else {
        setWarehouseData(null);
      }

      const cacheKey = buildCacheKey("pos:payment-modes", params);
      const modes = await fetchWithCache(
        cacheKey,
        async () => {
          const response = await axios.get(
            API_CONFIG.apiUrl("/payment-modes"),
            { params }
          );
          if (response.status !== 200) {
            return [];
          }

          if (response.data.paymentModes) {
            return response.data.paymentModes;
          }
          if (response.data.payment_modes) {
            return response.data.payment_modes;
          }
          if (Array.isArray(response.data)) {
            return response.data;
          }
          return [];
        },
        modesPaiementDefault
      );

      const finalModes = Array.isArray(modes) && modes.length > 0
        ? modes
        : modesPaiementDefault;

      if (currentPosContext.current !== requestContext) return [];
      setPaymentModes(finalModes);
      return finalModes;
    } catch (error) {
      if (currentPosContext.current !== requestContext) return [];
      // Utiliser les modes de paiement par d?faut en cas d'erreur
      setPaymentModes(modesPaiementDefault);
      return modesPaiementDefault;
    } finally {
      if (currentPosContext.current === requestContext) setLoadingPaymentModes(false);
    }
  };

  // Checkout configuration keeps its existing contract. Catalogue/customer
  // reads are owned by the common resource hook and never reset checkout fields.
  useEffect(() => {
    const requestContext = catalogChoices.contextKey;
    const controller = new AbortController();
    const stillCurrent = () => !controller.signal.aborted && currentPosContext.current === requestContext;
    if (!canUsePos || !normalizedCompanyId || !normalizedWarehouseId || !user?.id) {
      setLoading(false); setTaxes([]); setPaymentModes([]); setPosPaymentRoutes([]);
      return () => controller.abort();
    }
    setLoading(true);
    setWarehouseData(null);
    setSelectedModePaiement(null);
    setPosPaymentRoutes([]);
    setPosPaymentRoutesError("");
    void Promise.all([
      fetchWithCache(buildCacheKey("pos:taxes", { companyId: normalizedCompanyId, warehouseId: normalizedWarehouseId, status: "active" }),
        async () => {
          const response = await axios.get(API_CONFIG.apiUrl("/taxes"), {
            params: { status: "active", company_id: normalizedCompanyId }, signal: controller.signal,
          });
          return response.data;
        }, { taxes: [] }),
      fetchPaymentModes({ companyId: normalizedCompanyId, warehouseId: normalizedWarehouseId }),
      fetchPosPaymentRoutes(normalizedWarehouseId),
    ]).then(([taxesData, modes, routes]) => {
      if (!stillCurrent()) return;
      setTaxes(taxesData.taxes?.filter((tax: Tax) => tax.status === "active") || []);
      setSelectedModePaiement(resolvePreferredPosPaymentMode(modes, routes));
    }).catch(() => {
      if (stillCurrent()) message.error("Impossible de charger la configuration d’encaissement.");
    }).finally(() => { if (stillCurrent()) setLoading(false); });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedCompanyId, normalizedWarehouseId, user?.id, canUsePos]);

  const clearRestoredSalesDraft = useCallback(() => {
    const returnContext = location.state?.stockReplenishmentReturn;
    if (returnContext?.source_type !== "pos") {
      setRestoredDraftMeta(null);
      return;
    }

    const finalizedDraftId = finalizeSalesReplenishmentDraft(
      returnContext.sales_draft_id
    );
    if (finalizedDraftId) {
      // Le contexte de navigation peut rester visible pendant un rendu après
      // la suppression. Garder l'identifiant traité empêche sa restauration.
      restoredDraftHandledRef.current = finalizedDraftId;
    }
    setRestoredDraftMeta(null);

    const nextState = { ...(location.state || {}) };
    delete nextState.stockReplenishmentReturn;
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      },
      {
        replace: true,
        state: Object.keys(nextState).length > 0 ? nextState : null,
      }
    );
  }, [
    location.hash,
    location.pathname,
    location.search,
    location.state,
    navigate,
  ]);

  useEffect(() => {
    const returnContext = location.state?.stockReplenishmentReturn;
    const draftId = returnContext?.sales_draft_id;
    if (
      returnContext?.source_type !== "pos" ||
      !draftId ||
      !catalogLoaded ||
      !user?.id ||
      !normalizedCompanyId ||
      !normalizedWarehouseId ||
      restoredDraftHandledRef.current === draftId
    ) {
      return;
    }

    restoredDraftHandledRef.current = draftId;
    const draft = loadSalesReplenishmentDraft(draftId);
    const scopeMatches =
      draft &&
      draft.source_type === "pos" &&
      Number(draft.user_id) === Number(user?.id) &&
      Number(draft.company_id) === Number(normalizedCompanyId) &&
      Number(draft.warehouse_id) === Number(normalizedWarehouseId);

    if (!canUsePos || !scopeMatches) {
      message.error(
        !canUsePos
          ? "Vous n'avez plus la permission d'utiliser le point de vente."
          : "Le brouillon du panier est expiré ou ne correspond plus à la session, à l'entreprise ou à l'établissement sélectionné."
      );
      clearRestoredSalesDraft();
      return;
    }

    const savedCart: CartItem[] = Array.isArray(draft.payload?.cart) ? draft.payload.cart : [];
    const reconciliation = {
      items: savedCart.filter((item) => Number(item.cartQuantity) > 0),
      missingItemCount: savedCart.filter((item) => !products.some((product) => product.id_produit === item.id_produit)).length,
      priceChangedCount: savedCart.filter((item) => {
        const current = products.find((product) => product.id_produit === item.id_produit);
        return current && Math.abs(current.prix_vente - item.prix_vente) > 1e-6;
      }).length,
    };
    if (reconciliation.items.length === 0) {
      message.error(
        "Aucun article du panier sauvegardé n'est encore disponible dans cet établissement."
      );
      clearRestoredSalesDraft();
      return;
    }

    const customerId = Number(draft.payload?.selected_customer_id) || null;
    const customerStillAvailable = customerId
      ? customers.some(
          (customer) => Number(customer.ID_Client) === Number(customerId)
        )
      : false;
    const taxId = Number(draft.payload?.selected_tax_id) || null;

    cartContext.current = catalogChoices.contextKey;
    setCart(reconciliation.items);
    setSelectedCustomer(customerId);
    setSelectedTax(
      taxes.find((tax) => Number(tax.id) === Number(taxId)) || null
    );
    setDiscount(Math.max(0, Number(draft.payload?.discount) || 0));
    setDiscountType(
      draft.payload?.discount_type === "percentage" ? "percentage" : "amount"
    );
    setDiscountPercentage(
      Math.max(0, Number(draft.payload?.discount_percentage) || 0)
    );
    const restoredCategoryId = draft.payload?.active_category_id;
    setActiveCategory(
      restoredCategoryId !== null &&
        restoredCategoryId !== undefined &&
        restoredCategoryId !== "" &&
        Number.isFinite(Number(restoredCategoryId))
        ? Number(restoredCategoryId)
        : null
    );
    setSelectedServerId(
      Number(draft.payload?.selected_server_id) > 0
        ? Number(draft.payload.selected_server_id)
        : null
    );
    setPrintReceipt(draft.payload?.print_receipt !== false);
    setPaidAmount(0);
    setAmountReceived(0);
    setShowCart(true);
    setRestoredDraftMeta({
      draftId,
      missingItemCount: reconciliation.missingItemCount,
      priceChangedCount: reconciliation.priceChangedCount,
    });

    if (customerId && !customerStillAvailable) {
      message.warning(
        "Le client du brouillon n'est plus disponible. Sélectionnez un client avant de valider."
      );
    } else {
      message.success("Panier POS restauré. Vos quantités et prix saisis sont conservés.");
    }
  }, [
    canUsePos,
    catalogChoices.contextKey,
    catalogLoaded,
    clearRestoredSalesDraft,
    customers,
    location.state,
    normalizedCompanyId,
    normalizedWarehouseId,
    products,
    taxes,
    user,
  ]);

  const selectedPosPaymentRoute = useMemo(() => {
    if (!selectedModePaiement) {
      return null;
    }

    return (
      posPaymentRoutes.find(
        (route) =>
          Number(route.payment_mode_id) === Number(selectedModePaiement)
      ) || null
    );
  }, [posPaymentRoutes, selectedModePaiement]);

  // Fermeture des résultats de recherche si clic en dehors
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const searchWrapper = document.getElementById("search-wrapper");
      if (searchWrapper && !searchWrapper.contains(event.target as Node)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Rafraîchir les produits quand le magasin sélectionné change
  useEffect(() => {
    // Vérifier si c'est le chargement initial
    if (suppressSelectionRollbackPromptRef.current) {
      suppressSelectionRollbackPromptRef.current = false;
      prevWarehouseRef.current = normalizedWarehouseId;
      prevCompanyRef.current = normalizedCompanyId;
      return;
    }

    const previousWarehouseId = prevWarehouseRef.current;
    const previousCompanyId = prevCompanyRef.current;
    const isInitialLoad =
      previousWarehouseId === null && previousCompanyId === null;
    const warehouseChanged = previousWarehouseId !== normalizedWarehouseId;
    const companyChanged = previousCompanyId !== normalizedCompanyId;

    // Si ce n'est pas le chargement initial et qu'il y a un changement de magasin
    if (!isInitialLoad && (warehouseChanged || companyChanged)) {
      setQuickClientModalOpen(false);
      setStockShortageItems([]);
      setScannedBarcode("");

      // Avertir l'utilisateur que le panier sera vidé si le panier n'est pas vide
      if (cart.length > 0) {
        Modal.confirm({
          title: companyChanged
            ? "Changement d'entreprise"
            : "Changement d'établissement",
          content:
            "Le changement de contexte va vider votre panier actuel. Voulez-vous continuer ?",
          onOk: () => {
            if (currentPosContext.current !== catalogChoices.contextKey) return;
            cartContext.current = catalogChoices.contextKey;
            setCart([]);
            setSelectedCustomer(null);
            setActiveCategory(null);
            setSearchTerm("");
          },
          onCancel: () => {
            suppressSelectionRollbackPromptRef.current = true;
            setSelectedCompany(previousCompanyId);
            setSelectedWarehouse(previousWarehouseId);
          },
        });
      } else {
        cartContext.current = catalogChoices.contextKey;
        setSelectedCustomer(null);
        setActiveCategory(null);
        setSearchTerm("");
      }
    } else if (isInitialLoad) {
      // Premier chargement, on ne fait rien de spécial car useEffect pour le chargement initial s'en occupe
    }

    // Mettre à jour la référence
    prevWarehouseRef.current = normalizedWarehouseId;
    prevCompanyRef.current = normalizedCompanyId;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    normalizedCompanyId,
    normalizedWarehouseId,
    cart.length,
    setSelectedCompany,
    setSelectedWarehouse,
  ]);

  // Gestion du clic sur une catégorie (par identifiant)
  const handleCategoryClick = (categoryId: number | null) => {
    setActiveCategory(categoryId);
  };

  const openStockShortageActions = (items: any[]) => {
    const normalizedItems = buildMissingStockItems(items);
    if (normalizedItems.length > 0) {
      setStockShortageItems(normalizedItems);
    }
  };

  const getCartStockShortages = (currentProducts: Product[] = products) =>
    buildMissingStockItems(
      cart.flatMap((item) => {
        if (item.item_type === "service") return [];
        const product = currentProducts.find(
          (candidate) => Number(candidate.id_produit) === Number(item.id_produit)
        );
        if (!product || !requiresStrictStockCheck(product)) return [];
        return [
          {
            ...product,
            product_id: product.product_id ?? product.id_produit,
            product_name: product.nom_produit,
            required_quantity: item.cartQuantity,
            available_quantity: product.quantite_stock,
          },
        ];
      })
    );

  const navigateToStockResolution = (
    method: "purchase" | "adjustment" | "production"
  ) => {
    const { productionItems, purchaseItems } =
      splitStockShortageItems(stockShortageItems);
    if (method === "purchase" && !canCreatePurchase) {
      message.error("Vous n'avez pas la permission de créer un achat.");
      return;
    }
    if (method === "production" && !canCreateProduction) {
      message.error(
        `Vous n'avez pas la permission de lancer la ${productionTerminology.costContext}.`
      );
      return;
    }

    const resolutionItems =
      method === "production"
        ? productionItems
        : method === "purchase"
          ? purchaseItems
          : stockShortageItems;
    if (resolutionItems.length === 0) {
      message.info(
        method === "production"
          ? "Aucun produit fabriqué ne nécessite de production."
          : "Aucun produit acheté ne nécessite de réapprovisionnement."
      );
      return;
    }
    if (method === "adjustment" && !canCreateStockAdjustment) {
      message.error(
        "Vous n'avez pas la permission de créer un ajustement de stock."
      );
      return;
    }

    const draftCart = mergePosDraftCartWithShortage(
      cart,
      stockShortageItems,
      products
    );
    const salesDraft = saveSalesReplenishmentDraft({
      sourceType: "pos",
      sourcePath: `${location.pathname}${location.search}`,
      userId: user?.id,
      companyId: normalizedCompanyId,
      warehouseId: normalizedWarehouseId,
      payload: {
        cart: draftCart,
        selected_customer_id: selectedCustomer,
        selected_tax_id: selectedTax?.id || null,
        discount,
        discount_type: discountType,
        discount_percentage: discountPercentage,
        active_category_id: activeCategory,
        selected_server_id: selectedServerId,
        print_receipt: printReceipt,
      },
    });

    if (!salesDraft) {
      message.error(
        "Impossible de sécuriser temporairement le panier. Le réapprovisionnement n'a pas été ouvert."
      );
      return;
    }

    const returnContext = {
      sales_draft_id: salesDraft.id,
      sales_draft_source: "pos",
    };

    if (method === "production") {
      const productionReplenishment =
        buildSalesProductionReplenishmentRequest({
          sourceType: "pos",
          sourceLabel: "panier du point de vente",
          sourcePath: `${location.pathname}${location.search}`,
          userId: user?.id,
          companyId: selectedCompany,
          warehouseId: selectedWarehouse,
          items: resolutionItems,
          metadata: { return_context: returnContext },
        });
      const productionNavigation =
        buildSalesProductionReplenishmentNavigation(
          productionReplenishment
        );

      if (!productionNavigation) {
        deleteSalesReplenishmentDraft(salesDraft.id);
        message.error(
          `Impossible de préparer le lancement de la ${productionTerminology.costContext}.`
        );
        return;
      }

      if (
        restoredDraftMeta?.draftId &&
        restoredDraftMeta.draftId !== salesDraft.id
      ) {
        deleteSalesReplenishmentDraft(restoredDraftMeta.draftId);
      }
      setStockShortageItems([]);
      navigate(productionNavigation.path, {
        state: productionNavigation.state,
      });
      return;
    }

    const replenishment = buildStockReplenishmentRequest({
      sourceType: "pos",
      sourceLabel: "panier du point de vente",
      sourcePath: `${location.pathname}${location.search}`,
      companyId: selectedCompany,
      warehouseId: selectedWarehouse,
      items: resolutionItems,
      metadata: { return_context: returnContext },
    });
    if (
      restoredDraftMeta?.draftId &&
      restoredDraftMeta.draftId !== salesDraft.id
    ) {
      deleteSalesReplenishmentDraft(restoredDraftMeta.draftId);
    }
    setStockShortageItems([]);
    navigate(
      method === "purchase"
        ? "/gestion-achats/achat"
        : "/gestion-stock?tab=adjustments",
      { state: { stockReplenishment: replenishment } }
    );
  };

  // Ajout d'un produit au panier avec contrôle du stock
  const handleAddToCart = (product: Product) => {
    if (!cartContextMatches) {
      message.warning("Confirmez le changement d’établissement ou revenez au contexte de votre panier.");
      return;
    }
    if (requiresStrictStockCheck(product) && product.quantite_stock == null) {
      message.warning("Le stock de cet article n’a pas pu être vérifié. Actualisez le catalogue.");
      return;
    }
    if (!cart.length) cartContext.current = catalogChoices.contextKey;
    // Vérifier la quantité déjà ajoutée
    const existingItem = cart.find(
      (item) => item.id_produit === product.id_produit
    );
    const currentCartQty = existingItem ? existingItem.cartQuantity : 0;
    if (
      requiresStrictStockCheck(product) &&
      currentCartQty >= (product.quantite_stock ?? 0)
    ) {
      openStockShortageActions([
        {
          ...product,
          product_id: product.product_id ?? product.id_produit,
          product_name: product.nom_produit,
          required_quantity: currentCartQty + 1,
          available_quantity: product.quantite_stock,
        },
      ]);
      return;
    }

    const cartItem: CartItem = {
      id_produit: product.id_produit,
      item_type: product.item_type || "product",
      product_id:
        product.item_type === "service"
          ? null
          : product.product_id ?? product.id_produit,
      service_id: product.service_id ?? null,
      nom_produit: product.nom_produit,
      prix_vente: product.prix_vente,
      image: product.image,
      id_categorie: product.id_categorie,
      stock_management_mode: product.stock_management_mode,
      sellable_without_production: product.sellable_without_production,
      code_qr: product.code_qr || "",
      duration_minutes: product.duration_minutes ?? null,
      cartQuantity: 1,
    };

    setCart((prevCart) => {
      const existing = prevCart.find(
        (item) => item.id_produit === product.id_produit
      );
      if (existing) {
        // Incrémenter la quantité si possible
        return prevCart.map((item) =>
          item.id_produit === product.id_produit
            ? { ...item, cartQuantity: item.cartQuantity + 1 }
            : item
        );
      }
      return [...prevCart, cartItem];
    });
    if (isMobile) {
      setShowCart(true);
    }
    void playBusinessAudioAlert("pos_cart_add");
    message.success(`${product.nom_produit} ajouté au panier`);
  };

  // Searches and barcode lookups are bounded server queries, independent of
  // the current category/page. Refetching updates choices, never the basket.
  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term); setScannedBarcode(""); setShowSearchResults(Boolean(term.trim()));
  }, []);
  useEffect(() => {
    setSearchResults(searchTerm.trim() && catalogChoices.searchReady ? catalogChoices.catalogProducts : []);
  }, [catalogChoices.catalogProducts, catalogChoices.searchReady, searchTerm]);

  // Sélection d'un résultat de recherche (ajout au panier)
  const handleSearchResultClick = (product: Product) => {
    handleAddToCart(product);
    setSearchTerm("");
    setScannedBarcode("");
    setSearchResults([]);
    setShowSearchResults(false);
  };

  // Vider le panier (avec confirmation)
  const handleEmptyCart = () => {
    if (cart.length === 0) return;
    Modal.confirm({
      title: "Vider le panier",
      content: "Êtes-vous sûr de vouloir vider le panier ?",
      onOk: () => {
        setCart([]);
        clearRestoredSalesDraft();
      },
    });
  };

  // Initialisation du scanner QR
  const initializeScanner = () => {
    const scanContext = catalogChoices.contextKey;
    if (scanner) {
      scanner.clear();
      setScanner(null);
    }
    const qrContainer = document.createElement("div");
    qrContainer.id = "qr-reader";
    const scannerContainer = document.querySelector("#scanner-container");
    if (scannerContainer) {
      scannerContainer.innerHTML = "";
      scannerContainer.appendChild(qrContainer);
      const newScanner = new Html5QrcodeScanner(
        "qr-reader",
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          rememberLastUsedCamera: true,
          showTorchButtonIfSupported: true,
        },
        false
      );
      newScanner.render(
        (decodedText: string) => {
          if (currentPosContext.current !== scanContext) return;
          setSearchTerm(decodedText);
          setScannedBarcode(decodedText);
          setShowSearchResults(true);
        },
        (error: any) => {
          // Erreur de scan silencieuse
        }
      );
      setScanner(newScanner);
      setIsScanning(true);
    }
  };

  // Modification de la quantité d'un article dans le panier avec contrôle sur le stock
  const handleQuantityChange = (productId: number, newQuantity: number) => {
    if (!cartContextMatches) return;
    const previousQuantity = cart.find((item) => item.id_produit === productId)?.cartQuantity || 0;
    // Récupérer les informations initiales du produit depuis le tableau products
    const productData = products.find((p) => p.id_produit === productId);
    if (newQuantity > previousQuantity && (!productData ||
      (requiresStrictStockCheck(productData) && productData.quantite_stock == null))) {
      message.warning("Cet article doit être revalidé. Vous pouvez réduire sa quantité ou le retirer du panier.");
      return;
    }
    if (newQuantity > previousQuantity && productData && requiresStrictStockCheck(productData) && newQuantity > (productData.quantite_stock ?? 0)) {
      openStockShortageActions([
        {
          ...productData,
          product_id: productData.product_id ?? productData.id_produit,
          product_name: productData.nom_produit,
          required_quantity: newQuantity,
          available_quantity: productData.quantite_stock,
        },
      ]);
      return;
    }
    setCart((prevCart) =>
      prevCart
        .map((item) =>
          item.id_produit === productId
            ? { ...item, cartQuantity: newQuantity }
            : item
        )
        .filter((item) => item.cartQuantity > 0)
    );
  };

  // Édition du prix d'un article
  const handleEditPrice = (productId: number, currentPrice: number) => {
    setEditingItem(productId);
    setEditPrice(currentPrice);
  };

  // Sauvegarde du nouveau prix
  const handleSavePrice = (productId: number) => {
    setCart((prevCart) =>
      prevCart.map((item) =>
        item.id_produit === productId
          ? { ...item, prix_vente: editPrice }
          : item
      )
    );
    setEditingItem(null);
  };

  // Suppression d'un article du panier
  const handleRemoveItem = (productId: number) => {
    setCart((prevCart) =>
      prevCart.filter((item) => item.id_produit !== productId)
    );
  };

  // Réinitialisation après validation de vente ou annulation
  const handleReset = () => {
    setCart([]);
    setSelectedCustomer(null);
    setSelectedTax(null);
    setDiscount(0);
    setDiscountPercentage(0);
    setDiscountType('amount');
    setPaidAmount(0); // Réinitialiser le montant payé
    setAmountReceived(0); // Réinitialiser le montant reçu
    setSelectedModePaiement(
      resolvePreferredPosPaymentMode(paymentModes, posPaymentRoutes)
    );
    setSearchTerm("");
    if (scanner) {
      scanner.clear();
      setIsScanning(false);
    }
    setOrderData(null);
    setResetCartAfterTicketClose(false);
    setTicketVisible(false);
    clearRestoredSalesDraft();
  };

  const selectedWarehouseRecord = useMemo(() => {
    const assignedWarehouses = Array.isArray(user?.assigned_warehouses)
      ? user?.assigned_warehouses ?? []
      : [];

    return (
      assignedWarehouses.find(
        (warehouse: any) => Number(warehouse.id) === normalizedWarehouseId
      ) || null
    );
  }, [normalizedWarehouseId, user?.assigned_warehouses]);

  const selectedCompanyName =
    selectedWarehouseRecord?.company_name ||
    (normalizedCompanyId
      ? `Entreprise #${normalizedCompanyId}`
      : "Entreprise non selectionnee");

  const selectedWarehouseName =
    warehouseData?.name ||
    selectedWarehouseRecord?.name ||
    (normalizedWarehouseId
      ? `Établissement #${normalizedWarehouseId}`
      : "Établissement non selectionne");

  const activeCategoryLabel = useMemo(() => {
    if (activeCategory === null) {
      return "Tous les produits";
    }

    return (
      categories.find((category) => category.id === activeCategory)
        ?.categorie || "Categorie active"
    );
  }, [activeCategory, categories]);

  const openTicketPreview = useCallback(
    (ticketPayload: any, options: { resetOnClose?: boolean } = {}) => {
      setOrderData(ticketPayload);
      setResetCartAfterTicketClose(Boolean(options.resetOnClose));
      setTicketVisible(true);
    },
    []
  );

  // --- Encaissement / conversion d'une commande restaurant en vente POS ---
  const openConvertModal = useCallback(
    (order: RestaurantOrderSummary) => {
      if (!order) return;
      if (isOffline()) {
        message.warning(
          "L'encaissement d'une commande n'est pas disponible en mode hors ligne."
        );
        return;
      }
      setConvertOrder(order);
      setConvertPayAmount(Number(order.total) || 0);
      setConvertPayModeId(
        selectedModePaiement ||
          resolvePreferredPosPaymentMode(paymentModes, posPaymentRoutes)
      );
    },
    [paymentModes, posPaymentRoutes, selectedModePaiement]
  );

  const closeConvertModal = useCallback(() => {
    if (convertSubmitting) return;
    setConvertOrder(null);
    setConvertPayModeId(null);
    setConvertPayAmount(0);
  }, [convertSubmitting]);

  const handleConfirmConvert = useCallback(async () => {
    if (!convertOrder || !normalizedCompanyId) return;
    const amount = Number(convertPayAmount) || 0;
    if (amount > 0 && !convertPayModeId) {
      message.warning("Veuillez selectionner un mode de paiement.");
      return;
    }

    const orderTotal = Number(convertOrder.total) || 0;

    const submit = async () => {
      setConvertSubmitting(true);
      try {
        const treasuryRoute = posPaymentRoutes.find(
          (route) => Number(route.payment_mode_id) === Number(convertPayModeId)
        );
        const response = await axios.post(
          API_CONFIG.apiUrl(
            `/restaurant/orders/${convertOrder.id}/convert-to-sale`
          ),
          {
            company_id: normalizedCompanyId,
            warehouse_id: convertOrder.warehouse_id || normalizedWarehouseId,
            payment:
              amount > 0
                ? {
                    amount,
                    payment_mode_id: convertPayModeId,
                    treasury_account_id:
                      treasuryRoute?.treasury_account_id || null,
                    date: new Date().toISOString().split("T")[0],
                  }
                : { amount: 0 },
          },
          { headers: getAuthHeaders() }
        );

        const data = response.data || {};
        const modeName =
          paymentModes.find((mode) => mode.id === convertPayModeId)?.name ||
          "Especes";
        const ticketData = {
          order: {
            id: data.orderId,
            verification_token:
              data.verification_token || data.order?.verification_token,
            invoice_number: data.invoice_number,
            source_order_number: data.source_order_number,
            order_date: new Date().toISOString(),
            total: data.total ?? data.order?.total ?? orderTotal,
            subtotal: data.subtotal ?? 0,
            tax_amount: data.tax_amount ?? 0,
            discount: 0,
            paid_amount: data.order?.paid_amount ?? amount,
            due_amount:
              data.order?.due_amount ?? Math.max(orderTotal - amount, 0),
            payment_mode: modeName,
            payment_status: data.order?.payment_status || "",
            order_status: data.order?.order_status || "delivered",
            warehouse_id: data.warehouse_id || convertOrder.warehouse_id,
            client_name: data.client_name || "Client de passage",
          },
          items: Array.isArray(data.ticket_items) ? data.ticket_items : [],
          customer: { name: data.client_name || "Client de passage" },
          staff_member: { name: user?.name || "Vendeur" },
          server: null,
        };

        message.success("Commande encaissee et convertie en vente.");
        setConvertOrder(null);
        setConvertPayModeId(null);
        setConvertPayAmount(0);
        openTicketPreview(ticketData, { resetOnClose: true });
        await fetchRestaurantOrders({ silent: true });
      } catch (error: any) {
        message.error(
          error?.response?.data?.error ||
            error?.response?.data?.details ||
            error?.message ||
            "Echec de l'encaissement de la commande."
        );
      } finally {
        setConvertSubmitting(false);
      }
    };

    if (amount > 0 && amount < orderTotal - 0.0001) {
      Modal.confirm({
        title: "Encaissement partiel",
        content: `Le montant saisi (${formatRestaurantMoney(
          amount
        )} CFA) est inferieur au total (${formatRestaurantMoney(
          orderTotal
        )} CFA). Confirmer l'encaissement partiel ?`,
        okText: "Confirmer",
        cancelText: "Annuler",
        onOk: submit,
      });
      return;
    }

    await submit();
  }, [
    convertOrder,
    convertPayAmount,
    convertPayModeId,
    fetchRestaurantOrders,
    normalizedCompanyId,
    normalizedWarehouseId,
    openTicketPreview,
    paymentModes,
    posPaymentRoutes,
    user,
  ]);

  const convertModalNode = (
    <Modal
      open={Boolean(convertOrder)}
      title={
        convertOrder
          ? `Encaisser la commande ${
              convertOrder.order_number || `#${convertOrder.id}`
            }`
          : "Encaisser la commande"
      }
      onCancel={closeConvertModal}
      onOk={handleConfirmConvert}
      confirmLoading={convertSubmitting}
      maskClosable={!convertSubmitting}
      okText="Encaisser et generer le ticket"
      cancelText="Annuler"
      destroyOnClose
    >
      {convertOrder && (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontWeight: 600,
            }}
          >
            <span>Total a payer</span>
            <span>{formatRestaurantMoney(convertOrder.total)} CFA</span>
          </div>

          <div>
            <Typography.Text>Mode de paiement</Typography.Text>
            <Select
              style={{ width: "100%", marginTop: 4 }}
              placeholder="Selectionner un mode de paiement"
              value={convertPayModeId || undefined}
              onChange={(value) => setConvertPayModeId(value)}
              options={paymentModes.map((mode: any) => ({
                value: mode.id,
                label: mode.name,
              }))}
            />
          </div>

          <div>
            <Typography.Text>Montant encaisse</Typography.Text>
            <InputNumber
              style={{ width: "100%", marginTop: 4 }}
              min={0}
              value={convertPayAmount}
              onChange={(value) => setConvertPayAmount(Number(value) || 0)}
              addonAfter="CFA"
            />
          </div>
        </Space>
      )}
    </Modal>
  );

  const handleTicketClose = useCallback(() => {
    if (resetCartAfterTicketClose) {
      handleReset();
      return;
    }

    setResetCartAfterTicketClose(false);
    setOrderData(null);
    setTicketVisible(false);
  }, [handleReset, resetCartAfterTicketClose]);

  const handleOpenReprintReceipt = useCallback(
    (receiptData: any) => {
      openTicketPreview(receiptData, { resetOnClose: false });
    },
    [openTicketPreview]
  );

  // Soumission de la vente
  const handleSubmit = async () => {
    if (submitInProgress.current) return;
    submitInProgress.current = true;
    const submittedDraftKey = currentDraftKey;
    const assertSubmission = () => {
      catalogChoices.assertContext();
      if (!cartContextMatches || currentDraft.current !== submittedDraftKey) {
        throw new Error("La saisie ou le contexte a changé pendant la vérification. Votre panier est conservé ; vérifiez-le puis réessayez.");
      }
    };
    try {
      setSubmitting(true);
      assertSubmission();
      if (!selectedCompany) {
        message.error("Veuillez sélectionner une entreprise");
        return;
      }

      if (!selectedWarehouse) {
        message.error("Veuillez sélectionner un établissement");
        return;
      }

      if (!selectedCustomer) {
        message.error("Veuillez sélectionner un client");
        return;
      }

      if (!cart.length) return;
      const currentProducts = isOffline() ? products : await catalogChoices.verifySelection();
      assertSubmission();
      const unknownStock = cart.some((item) => {
        const product = currentProducts.find((candidate) => candidate.id_produit === item.id_produit);
        return !product || (requiresStrictStockCheck(product) && !Number.isFinite(product.quantite_stock));
      });
      if (unknownStock) throw new Error("Les disponibilités de certains articles n’ont pas pu être vérifiées. Votre panier est conservé ; actualisez le catalogue.");
      const cartShortages = getCartStockShortages(currentProducts);
      if (cartShortages.length > 0) {
        setStockShortageItems(cartShortages);
        return;
      }

      if (calculatedDiscount > baseHT) {
        message.error("La remise ne peut pas dépasser le montant HT");
        return;
      }

      if (discountType === "percentage" && discountPercentage > 100) {
        message.error("Le pourcentage de remise ne peut pas dépasser 100%");
        return;
      }

      if (discountType === "percentage" && discountPercentage < 0) {
        message.error("Le pourcentage de remise ne peut pas être négatif");
        return;
      }

      if (discountType === "amount" && discount < 0) {
        message.error("Le montant de la remise ne peut pas être négatif");
        return;
      }

      let defaultOrderStatus = "delivered";
      try {
        if (selectedWarehouse) {
          const warehouse = await loadWarehouseData(
            selectedWarehouse?.id ?? selectedWarehouse
          );
          if (warehouse && warehouse.default_pos_order_status) {
            defaultOrderStatus = warehouse.default_pos_order_status || "delivered";
          }
        }
      } catch (error) {
      }

      const translatedOrderStatus = translateOrderStatus(defaultOrderStatus);
      const uniqueId = "TEMP-" + Date.now();

      const totalItems = cart.length;
      const totalQuantity = cart.reduce((sum, item) => sum + item.cartQuantity, 0);

      const warehouseId = selectedWarehouse?.id ?? selectedWarehouse;
      const companyId = selectedCompany?.id ?? selectedCompany;
      const offlineMode = isOffline();
      const selectedMode = selectedModePaiement
        ? paymentModes.find((mode) => mode.id === selectedModePaiement)
        : null;

      const montantBaseHT = baseHT;
      const safeManualDiscount = Math.max(0, calculatedDiscount || 0);
      let safeLoyaltyDiscount = 0;
      let safeLoyaltyDiscountRate = 0;
      let safeLoyaltyDiscountApplied = false;

      if (!offlineMode && warehouseId && selectedCustomer && montantBaseHT > 0) {
        const loyaltyEligibility = await fetchLoyaltyEligibility(
          Number(warehouseId),
          Number(selectedCustomer),
          montantBaseHT
        );

        if (
          loyaltyEligibility?.eligible &&
          Number(loyaltyEligibility.discount_amount || 0) > 0
        ) {
          const ordersCount = Number(
            loyaltyEligibility.orders_count ??
              loyaltyEligibility.periods_count ??
              0
          );
          const ordersRequired = Number(
            loyaltyEligibility.orders_required ??
              loyaltyEligibility.periods_required ??
              0
          );
          let shouldApplyLoyalty = true;
          if (loyaltyEligibility.prompt_on_sale) {
            shouldApplyLoyalty = await new Promise((resolve) => {
              Modal.confirm({
                title: "Remise fidélité disponible",
                content: (
                  <div>
                    <p>
                      Ce client est éligible à une remise fidélité de{" "}
                      <strong>
                        {Number(loyaltyEligibility.discount_rate || 0).toLocaleString()}%
                      </strong>
                      .
                    </p>
                    <p>
                      Progression:{" "}
                      <strong>
                        {ordersCount.toLocaleString()} / {ordersRequired.toLocaleString()}
                      </strong>{" "}
                      commandes sur la période.
                    </p>
                    <p>
                      Montant estimé: {" "}
                      <strong>
                        {Number(loyaltyEligibility.discount_amount || 0).toLocaleString()} CFA
                      </strong>
                    </p>
                    <p>Appliquer la remise fidélité sur cette vente ?</p>
                  </div>
                ),
                okText: "Appliquer",
                cancelText: "Ignorer",
                onOk: () => resolve(true),
                onCancel: () => resolve(false),
              });
            });
          }

          if (shouldApplyLoyalty) {
            safeLoyaltyDiscountApplied = true;
            safeLoyaltyDiscountRate = Math.max(
              0,
              Number(loyaltyEligibility.discount_rate || 0)
            );
            safeLoyaltyDiscount = Math.max(
              0,
              Number(loyaltyEligibility.discount_amount || 0)
            );
          }
        }
      }

      const effectiveLoyaltyDiscount = safeLoyaltyDiscountApplied
        ? safeLoyaltyDiscount
        : 0;
      const finalDiscount = Math.max(
        0,
        safeManualDiscount + effectiveLoyaltyDiscount
      );

      if (finalDiscount > montantBaseHT) {
        message.error("Le total des remises ne peut pas dépasser le montant HT");
        return;
      }

      const montantNetHTCalc = Math.max(0, montantBaseHT - finalDiscount);
      const montantTaxe = selectedTax?.rate
        ? montantNetHTCalc * (Number(selectedTax.rate) / 100)
        : 0;
      const montantTotalTTC = montantNetHTCalc + montantTaxe;

      const finalPaidAmount = paidAmount;
      const finalDueAmount = Math.max(0, montantTotalTTC - finalPaidAmount);
      const finalPaymentStatus =
        finalPaidAmount <= 0
          ? "Non payé"
          : finalPaidAmount >= montantTotalTTC
          ? "Payé"
          : "Partiellement pay?";

      if (amountReceived > 0 && amountReceived < montantTotalTTC) {
        const confirmResult = await new Promise((resolve) => {
          Modal.confirm({
            title: "Montant insuffisant",
            content: (
              <div>
                <p>
                  Le client n'a remis que <strong>{amountReceived.toLocaleString()} CFA</strong>
                </p>
                <p>
                  alors que le total est de <strong>{montantTotalTTC.toLocaleString()} CFA</strong>.
                </p>
                <p>
                  Montant manquant: {" "}
                  <strong style={{ color: "#ff4d4f" }}>
                    {(montantTotalTTC - amountReceived).toLocaleString()} CFA
                  </strong>
                </p>
                <br />
                <p>Voulez-vous continuer avec un paiement partiel ?</p>
              </div>
            ),
            okText: "Oui, continuer",
            cancelText: "Non, annuler",
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
          });
        });

        if (!confirmResult) {
          return;
        }
      }

      const saleData = {
        company_id: companyId,
        invoice_type: "standard",
        order_type: "POS",
        order_date: new Date().toISOString(),
        customer_id: selectedCustomer,
        warehouse_id: warehouseId,
        from_warehouse_id: warehouseId,
        user_id: selectedCustomer,
        staff_user_id: user?.id || null,
        server_id: serversFeatureEnabled ? selectedServerId || null : null,
        tax_id: selectedTax?.id || null,
        tax_rate: selectedTax?.rate || 0,
        tax_amount: montantTaxe,
        discount: finalDiscount,
        manual_discount: safeManualDiscount,
        loyalty_discount: effectiveLoyaltyDiscount,
        loyalty_discount_rate: safeLoyaltyDiscountApplied
          ? safeLoyaltyDiscountRate
          : 0,
        loyalty_discount_applied: safeLoyaltyDiscountApplied,
        shipping: 0,
        subtotal: montantBaseHT,
        total: montantTotalTTC,
        paid_amount: finalPaidAmount,
        due_amount: finalDueAmount,
        payment_status: finalPaymentStatus,
        payment_mode: selectedMode?.name || "Espèces",
        order_status: translatedOrderStatus,
        total_items: totalItems,
        total_quantity: totalQuantity,
        manual_invoice_number: await manualInvoiceNumber.prepare(),
        notes: "",
        terms_condition: "",
        is_deletable: 1,
        cancelled: 0,
        is_deleted: 0,
        is_converted: 0,
        items: cart.map((item) => ({
          item_type: item.item_type || "product",
          product_id:
            item.item_type === "service"
              ? null
              : item.product_id || item.id_produit,
          service_id: item.item_type === "service" ? item.service_id : null,
          item_name: item.nom_produit,
          warehouse_id: warehouseId,
          quantity: item.cartQuantity,
          unit_price: item.prix_vente,
          single_unit_price: item.prix_vente,
          unit_id: null,
          total_price: item.prix_vente * item.cartQuantity,
          subtotal: item.prix_vente * item.cartQuantity,
          tax_id: selectedTax?.id || null,
          tax_rate: selectedTax?.rate || 0,
          tax_type: selectedTax?.tax_type || null,
          tax_amount: selectedTax?.rate
            ? item.prix_vente * item.cartQuantity * (selectedTax.rate / 100)
            : 0,
          total_tax: selectedTax?.rate
            ? item.prix_vente * item.cartQuantity * (selectedTax.rate / 100)
            : 0,
          discount_rate: 0,
          total_discount: 0,
          original_order_id: null,
          original_order_item_id: null,
        })),
      };

      if (finalPaidAmount > 0) {
        if (!selectedModePaiement) {
          throw new Error("Mode de paiement non selectionne");
        }

        const selectedModeForPayment = paymentModes.find(
          (mode) => mode.id === selectedModePaiement
        );

        if (!selectedModeForPayment) {
          throw new Error(
            "Mode de paiement ID " +
              selectedModePaiement +
              " non trouve dans la liste disponible"
          );
        }
      }

      const checkoutPayload = {
        order: saleData,
        payment: {
          amount: finalPaidAmount,
          payment_mode_id: selectedModePaiement,
          treasury_account_id:
            selectedPosPaymentRoute?.treasury_account_id || null,
          notes: "Paiement effectue lors de la vente POS",
          staff_user_id: user?.id || null,
          date: new Date().toISOString().split("T")[0],
        },
      };

      let result = null;
      let offlineTempId = null;

      assertSubmission();
      if (offlineMode) {
        offlineTempId = buildTempId("pos-checkout");
        await enqueueRequest({
          method: "POST",
          url: API_CONFIG.apiUrl("/orders/pos-checkout"),
          body: { ...checkoutPayload, client_request_id: offlineTempId },
          meta: { type: "order", tempId: offlineTempId, orderType: "POS" },
        });
        result = {
          id: offlineTempId,
          orderId: offlineTempId,
          invoice_number: uniqueId,
          offline: true,
        };
      } else {
        const response = await axios.post(
          API_CONFIG.apiUrl("/orders/pos-checkout"),
          checkoutPayload,
          {
            timeout: 10000,
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              "X-Company-Id": String(catalogChoices.scope.companyId),
            },
          }
        );

        if (response.status !== 200 && response.status !== 201) {
          throw new Error("Erreur lors du checkout POS");
        }

        result = response.data;
      }
      // A late successful write belongs to its captured context. It must not
      // clear a cart edited meanwhile or a different company's current screen.
      if (!offlineMode) void catalogChoices.invalidate("sale");
      if (currentPosContext.current !== catalogChoices.contextKey || currentDraft.current !== submittedDraftKey) {
        message.success("La vente a été enregistrée. La saisie actuelle est conservée.");
        return;
      }
      if (offlineMode) {
        message.info("Vente enregistrée localement (en attente de synchronisation)");
      } else {
        message.success("Vente enregistrée avec succès!");
      }

      if (printReceipt) {
        if (offlineMode) {
          const localTicket = createLocalTicketData(saleData, result, [...cart]);
          openTicketPreview(localTicket, { resetOnClose: true });
        } else {
          try {
            const orderResponse = await axios.get(
              API_CONFIG.apiUrl("/orders/" + (result.orderId || result.id))
            );
            if (currentPosContext.current !== catalogChoices.contextKey || currentDraft.current !== submittedDraftKey) return;

            if (orderResponse.status === 200) {
              const orderData = orderResponse.data;

              const ticketDataForPrint = {
                order: {
                  id: result.orderId || result.id,
                  verification_token:
                    orderData.verification_token ||
                    result.verification_token ||
                    result.order?.verification_token,
                  invoice_number:
                    orderData.invoice_number || result.invoice_number || uniqueId,
                  order_date: new Date().toISOString(),
                  total: montantTotalTTC,
                  subtotal: montantBaseHT,
                  tax_amount: montantTaxe,
                  discount: finalDiscount,
                  paid_amount: finalPaidAmount,
                  due_amount: finalDueAmount,
                  payment_mode: selectedMode?.name || "Espèces",
                  payment_status: finalPaymentStatus,
                  order_status: translatedOrderStatus,
                  warehouse_id: warehouseId,
                  client_name:
                    customers.find((c) => c.ID_Client === selectedCustomer)
                      ?.Nom_Raison_Sociale ||
                    orderData.customer?.name ||
                    "Client inconnu",
                },
                items: cart.map((item) => ({
                  item_type: item.item_type || "product",
                  product_id:
                    item.item_type === "service"
                      ? null
                      : item.product_id || item.id_produit,
                  service_id:
                    item.item_type === "service" ? item.service_id : null,
                  nom_produit: item.nom_produit,
                  cartQuantity: item.cartQuantity,
                  prix_vente: item.prix_vente,
                  total_price: item.prix_vente * item.cartQuantity,
                })),
                customer: {
                  id: selectedCustomer,
                  name:
                    customers.find((c) => c.ID_Client === selectedCustomer)
                      ?.Nom_Raison_Sociale ||
                    orderData.customer?.name ||
                    "Client inconnu",
                  phone: orderData.customer?.phone || "",
                  email: orderData.customer?.email || "",
                  address: orderData.customer?.address || "",
                },
                warehouse: warehouseData,
                staff_member: {
                  name: user?.name || orderData.staff_name || "Vendeur",
                },
                server:
                  serversFeatureEnabled && selectedServerId
                    ? {
                        id: selectedServerId,
                        name:
                          posServers.find((s) => s.id === selectedServerId)
                            ?.name ||
                          orderData.server_name ||
                          "Serveur",
                      }
                    : null,
              };

              openTicketPreview(ticketDataForPrint, { resetOnClose: true });
            } else {
              const localTicket = createLocalTicketData(saleData, result, [...cart]);
              openTicketPreview(localTicket, { resetOnClose: true });
            }
          } catch (error) {
            const localTicket = createLocalTicketData(saleData, result, [...cart]);
            openTicketPreview(localTicket, { resetOnClose: true });
          }
        }
      }

      if (currentPosContext.current !== catalogChoices.contextKey || currentDraft.current !== submittedDraftKey) return;
      if (offlineMode) catalogChoices.applyOfflineSale(cart);

      setCart([]);
      setDiscount(0);
      setDiscountPercentage(0);
      manualInvoiceNumber.setValue("");
      setDiscountType("amount");
      setPaidAmount(0);
      setAmountReceived(0);
      catalogChoices.setCustomerSearch("");
      setShowClientForm(false);
      setShowSearchResults(false);
      clearRestoredSalesDraft();
    } catch (error: any) {
      const serverStockItems = axios.isAxiosError(error)
        ? error.response?.data?.stock_items
        : error?.stock_items;
      if (Array.isArray(serverStockItems) && serverStockItems.length > 0) {
        setStockShortageItems(serverStockItems);
        return;
      }
      const apiErrorMessage = axios.isAxiosError(error)
        ? error.response?.data?.error ||
          error.response?.data?.message ||
          error.response?.data?.details
        : null;

      message.error(
        "Erreur: " +
          (apiErrorMessage ||
            error?.message ||
            "Erreur inconnue lors de l'enregistrement")
      );
    } finally {
      submitInProgress.current = false;
      setSubmitting(false);
      if (loading) {
        setLoading(false);
      }
    }
  };

  const createLocalTicketData = (
    saleData: any,
    result: any,
    cartItems: any[]
  ) => {
    // Récupérer les informations de l'entrepôt sélectionné
    const currentWarehouseInfo = warehouseData || {
      // Utiliser warehouseData s'il est chargé, sinon fallback
      id: selectedWarehouse,
      name: "Votre Entreprise",
      address: "Adresse de l'entreprise",
      phone: "Téléphone",
      email: "Email",
      logo_url: "/logo-placeholder.png",
      default_pos_order_status: "delivered",
    };

    // Récupérer les informations du client
    const customerInfo = customers.find(
      (c) => c.ID_Client === selectedCustomer
    ) || {
      ID_Client: 0,
      Nom_Raison_Sociale: "Client",
      phone: "",
      email: "",
      address: "",
    };

    // Obtenir le statut de commande en français
    let orderStatus = "Livré"; // Valeur par défaut
    if (saleData.order_status) {
      orderStatus = saleData.order_status;
    } else if (currentWarehouseInfo.default_pos_order_status) {
      orderStatus = translateOrderStatus(
        currentWarehouseInfo.default_pos_order_status
      );
    }

    return {
      order: {
        id: result.orderId || result.id || Date.now(),
        verification_token:
          result.verification_token || result.order?.verification_token || null,
        invoice_number:
          result.invoice_number ||
          saleData.invoice_number ||
          `VENTE-${Date.now()}`,
        order_date: saleData.order_date || new Date().toISOString(),
        total: saleData.total,
        subtotal: saleData.subtotal,
        tax_amount: saleData.tax_amount || 0,
        discount: saleData.discount || 0,
        paid_amount: saleData.paid_amount,
        due_amount: saleData.due_amount || 0,
        payment_mode: saleData.payment_mode,
        payment_status: saleData.payment_status || "Non payé",
        order_status: orderStatus,
        warehouse_id: selectedWarehouse,
        client_name: customerInfo.Nom_Raison_Sociale, // Ajout du nom du client
      },
      items: cartItems.map((item) => ({
        product_id: item.id_produit,
        nom_produit: item.nom_produit,
        cartQuantity: item.cartQuantity,
        prix_vente: item.prix_vente,
        total_price: item.prix_vente * item.cartQuantity,
      })),
      customer: {
        id: customerInfo.ID_Client,
        name: customerInfo.Nom_Raison_Sociale,
        phone: customerInfo.phone || "",
        email: customerInfo.email || "",
        address: customerInfo.address || "",
      },
      warehouse: currentWarehouseInfo,
      staff_member: {
        // MODIFICATION: Utiliser le nom de l'utilisateur connecté
        name: user?.name || "Vendeur",
        // email: user?.email || "vendeur@example.com", // Optionnel
      },
      server: saleData.server_id
        ? {
            id: saleData.server_id,
            name:
              posServers.find((s) => s.id === saleData.server_id)?.name ||
              "Serveur",
          }
        : null,
    };
  };

  // --- Filtrage dynamique des produits par catégorie ---
  const filteredProducts = useMemo(() => {
    return catalogChoices.catalogProducts.filter((p) =>
      !requiresStrictStockCheck(p) || p.quantite_stock == null || p.quantite_stock >= 1);
  }, [catalogChoices.catalogProducts]);

  const shouldVirtualizeProducts =
    filteredProducts.length > PRODUCT_VIRTUALIZATION_THRESHOLD ||
    (filteredProducts.length > 0 && Boolean(catalogChoices.catalog.data?.firstPageHasMore));

  // ========== LOGIQUE DE CALCUL CORRIGÉE ==========
  // 1. Base HT initiale = Σ (quantité × PU HT)
  const baseHT = cart.reduce(
    (sum, item) => sum + item.prix_vente * item.cartQuantity,
    0
  );

  // 1.5. Calcul dynamique de la remise selon le type (montant ou pourcentage)
  const calculatedDiscount = useMemo(() => {
    if (discountType === 'percentage') {
      return baseHT * (discountPercentage / 100);
    }
    return discount; // Montant fixe
  }, [discountType, discountPercentage, discount, baseHT]);

  // 2. Montant net HT = Base HT – total_remises (remises commerciales avant taxe)
  const montantNetHT = baseHT - calculatedDiscount;

  // 3. Taxe = Montant net HT × taux_Taxe
  const taxAmount = useMemo(() => {
    if (!selectedTax?.rate || montantNetHT <= 0) return 0;
    return montantNetHT * (selectedTax.rate / 100);
  }, [selectedTax, montantNetHT]);

  // 4. Total TTC = Montant net HT + Taxe
  const total = montantNetHT + taxAmount;

  // 5. Montant restant après paiement = Total TTC - Montant payé
  const dueAmount = Math.max(0, total - paidAmount);

  // 6. Calcul automatique de la monnaie à rendre
  const changeAmount = useMemo(() => {
    return Math.max(0, amountReceived - total);
  }, [amountReceived, total]);

  // Pour compatibilité avec l'affichage (noms utilisés dans l'interface)
  const subtotal = baseHT; // Alias pour l'affichage
  const cartItemCount = useMemo(() => {
    return cart.reduce((sum, item) => sum + item.cartQuantity, 0);
  }, [cart]);
  const suggestedCashAmount = useMemo(
    () => getSuggestedCashAmount(total),
    [total]
  );
  const discountAndTaxSummary = useMemo(() => {
    const discountLabel =
      calculatedDiscount > 0
        ? `Remise ${calculatedDiscount.toLocaleString()} CFA`
        : "Sans remise";
    const taxLabel = selectedTax
      ? `${selectedTax.name} ${selectedTax.rate}%`
      : "Sans taxe";

    return `${discountLabel} · ${taxLabel}`;
  }, [calculatedDiscount, selectedTax]);

  const updateCashAmount = (nextAmount: number) => {
    const normalizedAmount =
      Number.isFinite(nextAmount) && nextAmount > 0 ? nextAmount : 0;
    setAmountReceived(normalizedAmount);
    setPaidAmount(Math.min(normalizedAmount, total));
  };

  const handleCashDigit = (digit: number) => {
    updateCashAmount(appendCashDigit(amountReceived, digit));
  };

  const canSubmitSale =
    canUsePos && cartContextMatches && cart.length > 0 && Boolean(selectedCustomer);
  const currentStockConflicts = catalogChoices.selectedProducts.status === "verified" ? getCartStockShortages() : [];
  const hasCatalogFeedback = Boolean(catalogChoices.catalog.error || catalogChoices.categories.error ||
    (cart.length > 0 && catalogChoices.selectedProducts.error) || catalogChoices.missingCustomer ||
    catalogChoices.missingProducts.length > 0 || currentStockConflicts.length > 0 || !cartContextMatches);
  const catalogFeedback = hasCatalogFeedback ? <>
    {(catalogChoices.catalog.error || catalogChoices.categories.error) && <BusinessResourceNotice label="catalogue" resource={{
      data: catalogChoices.catalog.data || catalogChoices.categories.data,
      loading: catalogChoices.catalog.loading || catalogChoices.categories.loading || !catalogChoices.searchReady,
      error: catalogChoices.catalog.error || catalogChoices.categories.error,
      refresh: catalogChoices.refreshAll,
    }} />}
    {cart.length > 0 && Boolean(catalogChoices.selectedProducts.error) && <BusinessResourceNotice label="articles du panier" resource={catalogChoices.selectedProducts} />}
    {(catalogChoices.missingCustomer || catalogChoices.missingProducts.length > 0 || currentStockConflicts.length > 0 || !cartContextMatches) &&
      <Alert type="warning" showIcon style={{ marginBottom: 12 }} message="Votre panier est conservé."
        description={!cartContextMatches ? "Confirmez le changement d’établissement ou revenez au contexte de ce panier."
          : [catalogChoices.missingCustomer ? "Le client sélectionné n’est plus disponible ; choisissez un autre client." : "",
            catalogChoices.missingProducts.length ? `${catalogChoices.missingProducts.map((item) => item.nom_produit).join(", ")} : article indisponible. Retirez ou remplacez les articles concernés.` : "",
            currentStockConflicts.length ? "Le stock actuel est insuffisant pour les quantités saisies. Réduisez-les ou préparez un réapprovisionnement." : ""].filter(Boolean).join(" ")}
        action={currentStockConflicts.length ? <Button size="small" onClick={() => openStockShortageActions(currentStockConflicts)}>Voir les manquants</Button> : null} />}
  </> : null;
  const customerFeedback = catalogChoices.entities.error
    ? <BusinessResourceNotice label="clients" resource={catalogChoices.entities} /> : null;
  const catalogActions = <Space wrap style={{ margin: "12px 0" }}>
    <Button size="small" icon={<ReloadOutlined />} loading={catalogChoices.catalog.loading}
      onClick={() => { void catalogChoices.refreshAll().catch(() => {}); }}>Actualiser le catalogue</Button>
  </Space>;
  const catalogEndRef = useRef<HTMLDivElement | null>(null);
  const loadMoreCatalogRef = useRef(catalogChoices.loadMoreCatalog);
  loadMoreCatalogRef.current = catalogChoices.loadMoreCatalog;
  useEffect(() => {
    const element = catalogEndRef.current;
    if (!element || (!isPosMobile && shouldVirtualizeProducts) || !catalogChoices.catalog.data?.hasMore ||
      catalogChoices.catalog.loading || catalogChoices.catalogLoadingMore || catalogChoices.catalogLoadMoreError ||
      typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) void loadMoreCatalogRef.current();
    }, { rootMargin: "0px 0px 240px 0px" });
    observer.observe(element);
    return () => observer.disconnect();
  }, [loading, showCart, isPosMobile, shouldVirtualizeProducts, catalogChoices.catalog.data, catalogChoices.catalog.loading,
    catalogChoices.catalogLoadingMore, catalogChoices.catalogLoadMoreError]);
  const catalogFooter = <div ref={catalogEndRef} aria-label="Suite du catalogue" style={{ minHeight: 1 }}>
    {catalogChoices.catalogLoadingMore
      ? <Space style={{ padding: 8 }} role="status"><Spin size="small" /><Text type="secondary">Chargement des articles…</Text></Space>
      : catalogChoices.catalogLoadMoreError
        ? <Space wrap style={{ padding: 8 }} role="alert"><Text type="danger">Impossible de charger la suite du catalogue.</Text>
          <Button size="small" onClick={() => { void catalogChoices.loadMoreCatalog(true); }}>Réessayer</Button></Space>
        : null}
  </div>;
  const customerMoreFeedback = catalogChoices.customerLoadingMore
    ? <Space style={{ padding: 8 }} role="status"><Spin size="small" /><Text type="secondary">Chargement des clients…</Text></Space>
    : catalogChoices.customerLoadMoreError
      ? <Space wrap style={{ padding: 8 }} role="alert"><Text type="danger">Impossible de charger la suite des clients.</Text>
        <Button size="small" onClick={() => { void catalogChoices.loadMoreCustomers(true); }}>Réessayer</Button></Space>
      : null;
  const handleCustomerScroll = (event: React.UIEvent<HTMLElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight <= 48) void catalogChoices.loadMoreCustomers();
  };
  const handleCustomerPickerOpen = () => { void catalogChoices.entities.refresh({ force: true }).catch(() => {}); };
  const restoredCartShortages = restoredDraftMeta
    ? getCartStockShortages()
    : [];
  const restoredDraftDetails = restoredDraftMeta
    ? [
        restoredDraftMeta.priceChangedCount > 0
          ? restoredDraftMeta.priceChangedCount +
            " prix catalogue modifié" +
            (restoredDraftMeta.priceChangedCount > 1 ? "s" : "")
          : null,
        restoredDraftMeta.missingItemCount > 0
          ? restoredDraftMeta.missingItemCount +
            " article" +
            (restoredDraftMeta.missingItemCount > 1 ? "s" : "") +
            " indisponible" +
            (restoredDraftMeta.missingItemCount > 1 ? "s" : "")
          : null,
      ].filter(Boolean)
    : [];
  const restoredDraftAlert = restoredDraftMeta ? (
    <Alert
      type={restoredCartShortages.length > 0 ? "warning" : "success"}
      showIcon
      message={
        restoredCartShortages.length > 0
          ? "Panier restauré — stock encore insuffisant"
          : "Panier restauré — stock désormais suffisant"
      }
      description={
        "Les quantités et prix saisis sont conservés ; les références et disponibilités sont relues. Les données d'encaissement n'ont pas été restaurées" +
        (restoredDraftDetails.length > 0
          ? ". " + restoredDraftDetails.join(" ; ")
          : "") +
        "."
      }
      action={
        restoredCartShortages.length > 0 ? (
          <Button
            size="small"
            onClick={() => openStockShortageActions(restoredCartShortages)}
          >
            Voir les manquants
          </Button>
        ) : null
      }
      style={{ margin: "12px 14px" }}
    />
  ) : null;

  const syncMobilePaymentAmount = (modeId: number | null) => {
    const mode = paymentModes.find((item) => item.id === modeId) || null;
    const nextAmount = mode && !isMobileCashMode(mode) ? total : 0;
    setAmountReceived(nextAmount);
    setPaidAmount(nextAmount);
  };

  const handleMobileSelectPaymentMode = (modeId: number) => {
    setSelectedModePaiement(modeId);
    syncMobilePaymentAmount(modeId);
  };

  const handleMobileAmountReceived = (amount: number) => {
    setAmountReceived(amount);
    setPaidAmount(Math.min(Math.max(amount, 0), total));
  };

  const closeMobileScanner = () => {
    if (scanner) {
      void scanner.clear().catch(() => undefined);
      setScanner(null);
    }
    setIsScanning(false);
  };

  const handleMobileScannerToggle = () => {
    if (isScanning) {
      closeMobileScanner();
      return;
    }

    setIsScanning(true);
    window.setTimeout(initializeScanner, 0);
  };
  // ========== FIN LOGIQUE DE CALCUL CORRIGÉE ==========

  // Détermination du statut de paiement
  const paymentStatus = useMemo(() => {
    if (paidAmount <= 0) return "Non payé";
    if (paidAmount >= total) return "Payé";
    return "Partiellement payé";
  }, [paidAmount, total]);
  const checkoutActionPresentation = useMemo(
    () =>
      getCheckoutActionPresentation({
        cartItemCount,
        totalAmount: total,
        paidAmount,
        dueAmount,
      }),
    [cartItemCount, dueAmount, paidAmount, total]
  );

  // Conversion du statut de commande en français
  const translateOrderStatus = (status: string): string => {
    switch (status.toLowerCase()) {
      case "pending":
        return "En attente";
      case "delivered":
        return "Livré";
      case "ordered":
        return "Commandé";
      case "processing":
        return "En traitement";
      case "completed":
        return "Terminé";
      default:
        return "Livré"; // Valeur par défaut
    }
  };

  if (isPosMobile) {
    return (
      <>
        {restoredDraftAlert}
        <GestionPosMobile
          onMenu={outletContext?.openMobileMenu}
          companyName={selectedCompanyName}
          warehouseName={selectedWarehouseName}
          cashierName={user?.name || "Vendeur"}
          loading={loading}
          catalogLoading={catalogChoices.catalog.loading || !catalogChoices.searchReady}
          catalogUnavailable={Boolean(normalizedCompanyId && normalizedWarehouseId) && !catalogChoices.catalog.data}
          catalogFeedback={catalogFeedback}
          catalogActions={catalogActions}
          catalogFooter={catalogFooter}
          products={filteredProducts}
          categories={categories}
          activeCategory={activeCategory}
          onSelectCategory={handleCategoryClick}
          searchTerm={searchTerm}
          onSearch={handleSearch}
          searchResults={searchResults}
          showSearchResults={showSearchResults}
          onSearchResultClick={handleSearchResultClick}
          onScan={handleMobileScannerToggle}
          onAddToCart={handleAddToCart}
          resolveImage={(product) =>
            normalizeImageUrl(product.image, API_URL) || getImagePlaceholder()
          }
          cart={cart}
          cartItemCount={cartItemCount}
          subtotal={subtotal}
          discount={calculatedDiscount}
          taxLabel={selectedTax ? `TVA ${selectedTax.rate} %` : "Taxe"}
          taxAmount={taxAmount}
          total={total}
          onQuantityChange={handleQuantityChange}
          onRemoveItem={handleRemoveItem}
          onEmptyCart={handleEmptyCart}
          selectedCustomer={selectedCustomer}
          customers={customers}
          customerSearch={catalogChoices.customerSearch}
          onCustomerSearch={catalogChoices.setCustomerSearch}
          onCustomerPickerOpen={handleCustomerPickerOpen}
          customerLoading={catalogChoices.entities.loading}
          customerFeedback={customerFeedback}
          customerMoreFeedback={customerMoreFeedback}
          onCustomerScroll={handleCustomerScroll}
          onSelectCustomer={(customerId) => setSelectedCustomer(customerId)}
          onQuickClient={() => setQuickClientModalOpen(true)}
          canCreateClient={canCreateClient}
          paymentModes={paymentModes}
          selectedModePaiement={selectedModePaiement}
          onSelectMode={handleMobileSelectPaymentMode}
          amountReceived={amountReceived}
          onAmountReceived={handleMobileAmountReceived}
          changeAmount={changeAmount}
          invoiceNumberField={<ManualInvoiceNumberField control={manualInvoiceNumber} disabled={submitting} />}
          onStartPayment={() => { syncMobilePaymentAmount(selectedModePaiement); void manualInvoiceNumber.resource.refresh({ force: true }).catch(() => {}); }}
          onSubmit={handleSubmit}
          submitting={submitting}
          canSubmit={canSubmitSale}
          onOpenOrders={
            normalizedCompanyId && normalizedWarehouseId
              ? handleOpenRestaurantOrdersPanel
              : undefined
          }
          ordersBadge={restaurantAttentionCount}
          onOpenReprint={
            normalizedCompanyId && normalizedWarehouseId
              ? () => setReprintCenterOpen(true)
              : undefined
          }
          serversEnabled={serversFeatureEnabled}
          servers={posServers}
          selectedServerId={selectedServerId}
          onSelectServer={setSelectedServerId}
          onManageServers={() => setServerManagerOpen(true)}
        />

        <Modal
          open={isScanning}
          title="Scanner un produit"
          footer={null}
          onCancel={closeMobileScanner}
          destroyOnClose
        >
          <div id="scanner-container" style={{ margin: "auto", maxWidth: 320 }} />
        </Modal>

        <TicketCaisse
          visible={ticketVisible}
          order={orderData}
          onClose={handleTicketClose}
        />

        <RestaurantOrdersPosPanel
          open={restaurantOrdersPanelOpen}
          onClose={() => setRestaurantOrdersPanelOpen(false)}
          orders={activeRestaurantOrders}
          loading={restaurantOrdersLoading}
          error={restaurantOrdersError}
          selectedOrderId={selectedRestaurantOrderId}
          onSelectOrder={setSelectedRestaurantOrderId}
          onRefresh={() => fetchRestaurantOrders({ silent: false })}
          onAction={handleRestaurantOrderAction}
          onConvert={openConvertModal}
          convertDisabled={convertSubmitting}
          actionLoading={restaurantActionLoading}
          isMobile
        />

        <PosReprintCenter
          open={reprintCenterOpen}
          onClose={() => setReprintCenterOpen(false)}
          companyId={normalizedCompanyId}
          warehouseId={normalizedWarehouseId}
          warehouseData={warehouseData}
          customers={customers}
          isMobile
          onOpenReceipt={handleOpenReprintReceipt}
        />

        <QuickClientModal
          isOpen={quickClientModalOpen}
          onClose={() => setQuickClientModalOpen(false)}
          onSuccess={handleQuickClientSuccess}
          currentWarehouseId={selectedWarehouse}
        />

        <ServerManagerModal
          open={serverManagerOpen}
          onClose={() => setServerManagerOpen(false)}
          companyId={normalizedCompanyId}
          warehouseId={normalizedWarehouseId}
          onChanged={() => fetchPosServers()}
        />

        <StockShortageActionsModal
          open={stockShortageItems.length > 0}
          title="Approvisionner les produits manquants pour la vente"
          items={stockShortageItems}
          canPurchase={canCreatePurchase}
          canAdjust={canCreateStockAdjustment}
          canProduce={canCreateProduction}
          productionTerm={productionTerminology.costContext}
          productionActionLabel={`${productionTerminology.launchAction} — recommandé`}
          onCancel={() => setStockShortageItems([])}
          onPurchase={() => navigateToStockResolution("purchase")}
          onAdjustment={() => navigateToStockResolution("adjustment")}
          onProduction={() => navigateToStockResolution("production")}
        />

        {convertModalNode}
      </>
    );
  }

  return (
    <Layout className="pos-shell" style={{ minHeight: "100dvh" }}>
      {/* En-tête */}
      <Header
        className="pos-shell__header"
        style={{
          background: "var(--bg-primary)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: isMobile ? "14px" : "0 20px",
          borderBottom: "1px solid var(--border-color)",
          height: "auto",
          lineHeight: "normal",
        }}
      >
        <div className="pos-shell__header-main">
          <div className="pos-shell__title-block">
            <div className="pos-shell__title-row">
              <div className="pos-shell__title">Point de Vente</div>
              <div className="pos-shell__total-chip">
                {cartItemCount} art. | {total.toLocaleString()} CFA
              </div>
            </div>
            <div className="pos-shell__context">
              <span className="pos-shell__context-chip">
                {selectedCompanyName}
              </span>
              <span className="pos-shell__context-chip">
                {selectedWarehouseName}
              </span>
            </div>
          </div>
          <div className="pos-shell__actions">
            <div className="pos-shell__action-row">
              <Button
                className="pos-shell__reprint-button"
                icon={<PrinterOutlined />}
                onClick={() => setReprintCenterOpen(true)}
                disabled={!normalizedCompanyId || !normalizedWarehouseId}
              >
                Réimpressions
              </Button>
              <Badge
                count={restaurantAttentionCount}
                size="small"
                overflowCount={99}
                offset={[-2, 2]}
              >
                <Button
                  className="pos-shell__reprint-button pos-shell__orders-button"
                  icon={<UnorderedListOutlined />}
                  onClick={handleOpenRestaurantOrdersPanel}
                  disabled={!normalizedCompanyId || !normalizedWarehouseId}
                >
                  Commandes
                </Button>
              </Badge>
            </div>
            <div className="pos-shell__audio-toggle">
              <BusinessAudioToggle alertType="pos_cart_add" label="Sons POS" />
            </div>
          </div>
        </div>
      </Header>
      {restoredDraftAlert}
      {catalogFeedback && <div style={{ margin: "12px 20px 0" }}>{catalogFeedback}</div>}
      {/* Contenu principal */}
      <Layout>
        <Content
          className="pos-shell__content"
          style={{
            padding: isMobile ? "16px 14px 156px" : "20px",
            background: "var(--bg-secondary)",
          }}
        >
          {loading ? (
            <Spin tip="Chargement..." size="large" />
          ) : (
            <Row gutter={[16, 16]}>
              {/* Section Produits */}
              {(!isMobile || !showCart) && (
              <Col xs={24} lg={14} xl={15} xxl={16}>
                {!selectedWarehouse ? (
                  <Card>
                    <p style={{ textAlign: "center", padding: "20px" }}>
                      Veuillez sélectionner un établissement dans l'en-tête pour
                      afficher les produits.
                    </p>
                  </Card>
                ) : (
                  <>
                    <div className="pos-products-toolbar">
                      <div className="pos-products-toolbar__meta">
                        <span className="pos-products-toolbar__meta-chip">
                          {filteredProducts.length} articles visibles
                        </span>
                        <span className="pos-products-toolbar__meta-chip">
                          {activeCategoryLabel}
                        </span>
                        <span className="pos-products-toolbar__meta-chip">
                          {selectedWarehouseName}
                        </span>
                      </div>
                    {/* Barre de recherche et scanner */}
                    <Row
                      className="pos-products-toolbar__search-row"
                      gutter={8}
                      align="middle"
                      style={{ marginBottom: 0 }}
                    >
                      <Col xs={24} sm={16} md={18}>
                        <div id="search-container">
                          <Input
                            placeholder="Rechercher un produit ou un service..."
                            maxLength={120}
                            prefix={<SearchOutlined />}
                            value={searchTerm}
                            onChange={(e) => handleSearch(e.target.value)}
                            className="w-full" // Assure que l'input prend toute la largeur de sa colonne
                          />
                          {showSearchResults && searchResults.length > 0 && (
                            <div className="search-dropdown">
                              {searchResults.map((product) => (
                                <div
                                  key={product.id_produit}
                                  className="pos-search-result"
                                  style={{
                                    padding: "8px",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                  onClick={() =>
                                    handleSearchResultClick(product)
                                  }
                                >
                                  <img
                                    src={normalizeImageUrl(product.image, API_URL)}
                                    alt={product.nom_produit}
                                    style={{
                                      width: "20px",
                                      height: "20px",
                                      objectFit: "cover",
                                      marginRight: 8,
                                    }}
                                    onError={(event) => {
                                      event.currentTarget.src = getImagePlaceholder(
                                        product.nom_produit
                                      );
                                    }}
                                  />
                                  <div>
                                    <div style={{ fontWeight: "bold", color: "var(--text-primary)" }}>
                                      {product.nom_produit}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        color: "var(--text-secondary)",
                                      }}
                                    >
                                      {product.prix_vente.toLocaleString()} CFA
                                      - {renderStockInfo(product)}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </Col>
                      <Col xs={24} sm={8} md={6}>
                        <Button
                          icon={<QrcodeOutlined />}
                          type={isScanning ? "primary" : "default"}
                          onClick={() => {
                            if (isScanning) {
                              if (scanner) {
                                scanner.clear();
                                setScanner(null);
                              }
                              setIsScanning(false);
                            } else {
                              initializeScanner();
                            }
                          }}
                          className="pos-toolbar-button w-full sm:w-auto"
                        >
                          {isScanning ? "Arrêter" : "Scanner"}
                        </Button>
                      </Col>
                    </Row>
                    {isScanning && (
                      <div
                        className="pos-products-toolbar__scanner"
                        style={{
                          marginBottom: 16,
                          background: "var(--bg-primary)",
                          padding: 16,
                          borderRadius: 4,
                          textAlign: "center",
                          border: "1px solid var(--border-color)",
                        }}
                      >
                        <div
                          id="scanner-container"
                          style={{ margin: "auto", maxWidth: 300 }}
                        ></div>
                      </div>
                    )}
                    {/* Menu des catégories - barre coulissante horizontalement */}
                    <Row
                      className="pos-products-toolbar__categories"
                      style={{
                        marginBottom: 16,
                        overflowX: "auto",
                        whiteSpace: "nowrap",
                        padding: "0 8px", // Ajout d'un padding pour éviter que les boutons collent aux bords
                      }}
                      wrap={false}
                    >
                      {!selectedCompany ? (
                        <Col
                          style={{ display: "inline-block", marginRight: 8 }}
                        >
                          <Button className="pos-category-button" disabled>
                            Sélectionnez d'abord une entreprise
                          </Button>
                        </Col>
                      ) : categories.length === 0 ? (
                        <Col
                          style={{ display: "inline-block", marginRight: 8 }}
                        >
                          <Button className="pos-category-button" disabled>
                            Aucune catégorie disponible
                          </Button>
                        </Col>
                      ) : (
                        <>
                          <Col
                            style={{ display: "inline-block", marginRight: 8 }}
                          >
                            <Button
                              className="pos-category-button"
                              type={
                                activeCategory === null ? "primary" : "default"
                              }
                              onClick={() => handleCategoryClick(null)}
                            >
                              Tous les produits
                            </Button>
                          </Col>
                          {categories.map((category) => (
                            <Col
                              key={category.id}
                              style={{
                                display: "inline-block",
                                marginRight: 8,
                              }}
                            >
                              <Button
                                className="pos-category-button"
                                type={
                                  activeCategory === category.id
                                    ? "primary"
                                    : "default"
                                }
                                onClick={() => handleCategoryClick(category.id)}
                              >
                                {category.categorie}
                              </Button>
                            </Col>
                          ))}
                        </>
                      )}
                    </Row>
                    </div>
                    {catalogActions}
                    {/* Grille des produits */}
                    {shouldVirtualizeProducts ? (
                      <VirtualizedProductGrid
                        products={filteredProducts}
                        apiBaseUrl={API_URL}
                        onProductClick={handleAddToCart}
                        onEndReached={() => { void catalogChoices.loadMoreCatalog(); }}
                        bottomOffset={isMobile ? 164 : 24}
                        layoutKey={isScanning ? "scan" : "noscan"}
                      />
                    ) : (
                    <Row className="pos-products-grid" gutter={[16, 16]}>
                      {filteredProducts.map((product) => (
                        <Col
                          key={product.id_produit}
                          xs={12} // 2 produits par ligne
                          sm={8} // 3 produits par ligne
                          md={6} // 4 produits par ligne
                          lg={4} // 6 produits par ligne
                        >
                          <Card
                            className="pos-product-card"
                            hoverable
                            role="button"
                            tabIndex={0}
                            aria-label={`Ajouter ${product.nom_produit} au panier`}
                            cover={
                              <img
                                src={
                                  normalizeImageUrl(product.image, API_URL)
                                }
                                alt={product.nom_produit}
                                loading="lazy"
                                decoding="async"
                                style={{
                                  height: isMobile ? 132 : 150,
                                  objectFit: "cover",
                                }}
                                onError={(e: any) => {
                                  e.currentTarget.src = getImagePlaceholder(product.nom_produit);
                                }}
                              />
                            }
                            onClick={() => handleAddToCart(product)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault(); handleAddToCart(product);
                              }
                            }}
                            style={{ height: "100%" }}
                          >
                            <Card.Meta
                              title={product.nom_produit}
                              description={
                                <div>
                                  {product.prix_vente.toLocaleString()} CFA
                                  <br />
                                  {renderStockInfo(product)}
                                </div>
                              }
                            />
                          </Card>
                        </Col>
                      ))}
                    </Row>
                    )}
                    {catalogFooter}
                  </>
                )}
              </Col>
              )}
              {/* Section Panier */}
              {(!isMobile || showCart) && (
              <Col xs={24} lg={10} xl={9} xxl={8}>
                <Card
                  className="pos-cart-card"
                  title={
                    <div className="pos-cart-card__title">
                      <span className="pos-cart-card__heading">
                        <ShoppingCartOutlined aria-hidden="true" />
                        <span>
                          Panier <span>({cartItemCount})</span>
                        </span>
                      </span>
                      {cart.length > 0 && (
                        <Button
                          className="pos-cart-clear"
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={handleEmptyCart}
                        >
                          Vider
                        </Button>
                      )}
                    </div>
                  }
                  extra={
                    isMobile && (
                      <Button
                        className="pos-cart-card__close"
                        icon={<CloseOutlined />}
                        onClick={() => setShowCart(false)}
                      />
                    )
                  }
                  style={{
                    marginBottom: 16,
                    position: isMobile ? "static" : "sticky",
                    top: isMobile ? "auto" : 92,
                    maxHeight: isMobile ? "auto" : "calc(100vh - 112px)",
                  }}
                >
                  <div className="pos-checkout-panel">
                    <section className="pos-checkout-customer">
                      <Form layout="vertical" requiredMark>
                        <Form.Item
                          className="pos-checkout-customer__field"
                          label="Client"
                          required
                        >
                          <div className="pos-client-control">
                            <Select
                              showSearch
                              virtual={false}
                              aria-label="Client de la vente"
                              placeholder={
                                !selectedCompany
                                  ? "Sélectionnez d'abord une entreprise"
                                  : !selectedWarehouse
                                  ? "Sélectionnez un établissement pour filtrer"
                                  : catalogChoices.entities.loading
                                  ? "Chargement des clients…"
                                  : !catalogChoices.entities.data
                                  ? "Les clients doivent être chargés"
                                  : customers.length === 0
                                  ? "Aucun client disponible"
                                  : "Sélectionner un client"
                              }
                              optionFilterProp="children"
                              filterOption={false}
                              onSearch={catalogChoices.setCustomerSearch}
                              onDropdownVisibleChange={(open) => { if (open) handleCustomerPickerOpen(); }}
                              onPopupScroll={handleCustomerScroll}
                              dropdownRender={(menu) => <>{customerFeedback}{menu}{customerMoreFeedback}</>}
                              value={selectedCustomer || undefined}
                              onChange={(value: number) =>
                                setSelectedCustomer(value)
                              }
                              disabled={!selectedCompany || !selectedWarehouse || !canUsePos}
                              loading={catalogChoices.entities.loading}
                            >
                              {customers.map((customer) => (
                                <Option
                                  key={customer.ID_Client}
                                  value={customer.ID_Client}
                                >
                                  {customer.Nom_Raison_Sociale}
                                </Option>
                              ))}
                            </Select>
                            <Tooltip
                              title={
                                !selectedCompany
                                  ? "Sélectionnez d'abord une entreprise"
                                  : !canCreateClient
                                  ? "Permission de création des clients requise"
                                  : "Créer rapidement un client"
                              }
                            >
                              <Button
                                type="default"
                                icon={<PlusOutlined />}
                                aria-label="Créer rapidement un client"
                                onClick={() => setQuickClientModalOpen(true)}
                                disabled={!selectedCompany || !canCreateClient}
                              />
                            </Tooltip>
                          </div>
                          {customerFeedback}
                        </Form.Item>
                      </Form>
                      {!selectedCustomer && cart.length > 0 && (
                        <div className="pos-checkout-customer__hint" role="status">
                          <ExclamationCircleOutlined aria-hidden="true" />
                          Requis pour encaisser cette vente.
                        </div>
                      )}
                    </section>

                    <section
                      className="pos-cart-items"
                      aria-labelledby="pos-cart-items-title"
                    >
                      <div className="pos-checkout-section-heading">
                        <h3 id="pos-cart-items-title">Articles</h3>
                        <span>
                          {cartItemCount} article{cartItemCount > 1 ? "s" : ""}
                        </span>
                      </div>
                      <div className="pos-cart-list">
                        <List
                          rowKey={(item) => item.id_produit}
                          dataSource={cart}
                          locale={{
                            emptyText: (
                              <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description="Le panier est vide"
                              />
                            ),
                          }}
                          renderItem={(item) => (
                            <List.Item className="pos-cart-item">
                              <PosCartProductThumbnail
                                imagePath={item.image}
                                productName={item.nom_produit}
                              />
                              <Tooltip title={item.nom_produit} placement="topLeft">
                                <div className="pos-cart-item__identity">
                                  <strong>{item.nom_produit}</strong>
                                  <span>
                                    {item.prix_vente.toLocaleString()} CFA / unité
                                  </span>
                                </div>
                              </Tooltip>
                              <div
                                className="pos-cart-item__quantity"
                                role="group"
                                aria-label={`Quantité de ${item.nom_produit}`}
                              >
                                <Button
                                  icon={<MinusOutlined />}
                                  aria-label={`Retirer une unité de ${item.nom_produit}`}
                                  onClick={() =>
                                    handleQuantityChange(
                                      item.id_produit,
                                      item.cartQuantity - 1
                                    )
                                  }
                                />
                                <InputNumber
                                  controls={false}
                                  min={1}
                                  value={item.cartQuantity}
                                  aria-label={`Quantité saisie pour ${item.nom_produit}`}
                                  onChange={(value) =>
                                    handleQuantityChange(
                                      item.id_produit,
                                      Number(value) || 1
                                    )
                                  }
                                />
                                <Button
                                  icon={<PlusOutlined />}
                                  aria-label={`Ajouter une unité de ${item.nom_produit}`}
                                  onClick={() =>
                                    handleQuantityChange(
                                      item.id_produit,
                                      item.cartQuantity + 1
                                    )
                                  }
                                />
                              </div>
                              <div className="pos-cart-item__price">
                                {editingItem === item.id_produit ? (
                                  <InputNumber
                                    className="pos-cart-item__price-input"
                                    autoFocus
                                    controls={false}
                                    min={0}
                                    value={editPrice}
                                    aria-label={`Prix unitaire de ${item.nom_produit}`}
                                    onChange={(value) =>
                                      setEditPrice(Number(value) || 0)
                                    }
                                    onBlur={() =>
                                      handleSavePrice(item.id_produit)
                                    }
                                    onPressEnter={() =>
                                      handleSavePrice(item.id_produit)
                                    }
                                  />
                                ) : (
                                  <>
                                    <strong>
                                      {(
                                        item.prix_vente * item.cartQuantity
                                      ).toLocaleString()} CFA
                                    </strong>
                                    <Tooltip title="Modifier le prix unitaire">
                                      <Button
                                        type="text"
                                        size="small"
                                        icon={<EditOutlined />}
                                        aria-label={`Modifier le prix de ${item.nom_produit}`}
                                        onClick={() =>
                                          handleEditPrice(
                                            item.id_produit,
                                            item.prix_unitaire_HT ??
                                              item.prix_vente
                                          )
                                        }
                                      />
                                    </Tooltip>
                                  </>
                                )}
                              </div>
                              <Tooltip title="Retirer l'article">
                                <Button
                                  className="pos-cart-item__delete"
                                  icon={<DeleteOutlined />}
                                  aria-label={`Retirer ${item.nom_produit} du panier`}
                                  onClick={() =>
                                    handleRemoveItem(item.id_produit)
                                  }
                                  type="text"
                                  danger
                                />
                              </Tooltip>
                            </List.Item>
                          )}
                        />
                      </div>
                    </section>
                    <Collapse
                      className="pos-sale-options"
                      bordered={false}
                      expandIconPosition="end"
                      items={[
                        {
                          key: "discount-tax",
                          label: (
                            <div className="pos-sale-options__label">
                              <span>
                                <PercentageOutlined aria-hidden="true" />
                                Remise et taxe
                              </span>
                              <small>{discountAndTaxSummary}</small>
                            </div>
                          ),
                          children: (
                            <div className="pos-sale-options__content">
                              <Row gutter={12}>
                                <Col xs={24} sm={12}>
                                  <Form.Item label="Type de remise">
                                    <Select
                                      value={discountType}
                                      onChange={(
                                        value: 'amount' | 'percentage'
                                      ) => {
                                        setDiscountType(value);
                                        if (value === 'percentage') {
                                          setDiscount(0);
                                        } else {
                                          setDiscountPercentage(0);
                                        }
                                      }}
                                    >
                                      <Option value="amount">Montant (CFA)</Option>
                                      <Option value="percentage">
                                        Pourcentage (%)
                                      </Option>
                                    </Select>
                                  </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                  {discountType === 'amount' ? (
                                    <Form.Item label="Remise (CFA)">
                                      <InputNumber
                                        controls={false}
                                        min={0}
                                        max={baseHT}
                                        value={discount}
                                        onChange={(value) =>
                                          setDiscount(Number(value) || 0)
                                        }
                                      />
                                    </Form.Item>
                                  ) : (
                                    <Form.Item label="Remise (%)">
                                      <InputNumber
                                        controls={false}
                                        min={0}
                                        max={100}
                                        value={discountPercentage}
                                        addonAfter="%"
                                        onChange={(value) =>
                                          setDiscountPercentage(
                                            Number(value) || 0
                                          )
                                        }
                                      />
                                    </Form.Item>
                                  )}
                                </Col>
                              </Row>
                              <Form.Item label="Taxe applicable">
                                <Select
                                  allowClear
                                  placeholder="Aucune taxe"
                                  value={selectedTax?.id || undefined}
                                  onClear={() => setSelectedTax(null)}
                                  onChange={(value?: number) => {
                                    const tax = filteredTaxes.find(
                                      (item) => item.id === value
                                    );
                                    setSelectedTax(tax || null);
                                  }}
                                >
                                  {filteredTaxes.map((tax) => (
                                    <Option key={tax.id} value={tax.id}>
                                      {tax.name} - {tax.rate}%{" "}
                                      {tax.rate < 0 && "(Déduction)"}
                                    </Option>
                                  ))}
                                </Select>
                              </Form.Item>
                              <div className="pos-sale-totals">
                                <span>Base HT</span>
                                <strong>{subtotal.toLocaleString()} CFA</strong>
                                <span>Remise</span>
                                <strong>
                                  -{calculatedDiscount.toLocaleString()} CFA
                                </strong>
                                <span>Montant net HT</span>
                                <strong>{montantNetHT.toLocaleString()} CFA</strong>
                                <span>Taxe</span>
                                <strong>{taxAmount.toLocaleString()} CFA</strong>
                              </div>
                            </div>
                          ),
                        },
                      ]}
                    />

                    <ManualInvoiceNumberField control={manualInvoiceNumber} disabled={submitting} />
                    <section
                      className="pos-payment"
                      aria-labelledby="pos-payment-title"
                    >
                      <div className="pos-checkout-section-heading pos-payment__heading">
                        <h3 id="pos-payment-title">
                          <DollarOutlined aria-hidden="true" />
                          Paiement
                        </h3>
                      </div>
                      <Form layout="vertical">
                        <Form.Item
                          className="pos-payment-mode"
                          label="Mode de paiement"
                        >
                          <Select
                            value={selectedModePaiement}
                            onChange={(value: number) =>
                              setSelectedModePaiement(value)
                            }
                          >
                            {paymentModes.map((mode) => (
                              <Option key={mode.id} value={mode.id}>
                                {mode.name}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Form>
                      <div
                        className={`pos-payment-route ${
                          posPaymentRoutesError
                            ? "pos-payment-route--error"
                            : selectedPosPaymentRoute
                            ? "pos-payment-route--ready"
                            : "pos-payment-route--warning"
                        }`}
                      >
                        {posPaymentRoutesError
                          ? posPaymentRoutesError
                          : selectedPosPaymentRoute?.treasury_account_name
                          ? `Compte de destination : ${selectedPosPaymentRoute.treasury_account_name}`
                          : loadingPosPaymentRoutes
                          ? "Chargement du compte de destination…"
                          : "Aucun routage spécifique : le compte par défaut sera utilisé s'il est disponible."}
                      </div>

                      <div className="pos-payment-grid">
                        <div className="pos-cash-entry">
                          <label htmlFor="pos-amount-received">
                            Montant reçu
                          </label>
                          <Input
                            id="pos-amount-received"
                            className="pos-cash-entry__input"
                            inputMode="numeric"
                            type="number"
                            min={0}
                            value={amountReceived}
                            onChange={(event) =>
                              updateCashAmount(
                                normalizeCashAmount(event.target.value)
                              )
                            }
                            suffix="CFA"
                          />
                          <div
                            className="pos-cash-keypad"
                            role="group"
                            aria-label="Pavé numérique du montant reçu"
                          >
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
                              <Button
                                key={digit}
                                onClick={() => handleCashDigit(digit)}
                                aria-label={`Ajouter le chiffre ${digit}`}
                              >
                                {digit}
                              </Button>
                            ))}
                            <Button
                              className="pos-cash-keypad__clear"
                              onClick={() => updateCashAmount(0)}
                            >
                              Effacer
                            </Button>
                            <Button
                              onClick={() => handleCashDigit(0)}
                              aria-label="Ajouter le chiffre 0"
                            >
                              0
                            </Button>
                            <Button
                              icon={<RollbackOutlined />}
                              onClick={() =>
                                updateCashAmount(
                                  removeLastCashDigit(amountReceived)
                                )
                              }
                              aria-label="Effacer le dernier chiffre"
                            />
                          </div>
                        </div>

                        <div className="pos-payment-summary" aria-live="polite">
                          <div className="pos-payment-summary__metric">
                            <span>Total à payer</span>
                            <strong>{total.toLocaleString()} CFA</strong>
                          </div>
                          <div className="pos-payment-summary__metric pos-payment-summary__metric--change">
                            <span>Monnaie à rendre</span>
                            <strong>{changeAmount.toLocaleString()} CFA</strong>
                          </div>
                          {dueAmount > 0 && paidAmount > 0 && (
                            <div className="pos-payment-summary__remaining">
                              Reste dû : <strong>{dueAmount.toLocaleString()} CFA</strong>
                            </div>
                          )}
                          <span
                            className={`pos-payment-status pos-payment-status--${
                              paymentStatus === "Payé"
                                ? "paid"
                                : paymentStatus === "Partiellement payé"
                                ? "partial"
                                : "unpaid"
                            }`}
                          >
                            {paymentStatus}
                          </span>
                          <div className="pos-payment-shortcuts">
                            <Button
                              block
                              onClick={() => updateCashAmount(total)}
                              disabled={total <= 0}
                            >
                              Montant exact
                            </Button>
                            <Button
                              block
                              onClick={() =>
                                updateCashAmount(suggestedCashAmount)
                              }
                              disabled={suggestedCashAmount <= 0}
                            >
                              {suggestedCashAmount > 0
                                ? `${suggestedCashAmount.toLocaleString()} CFA`
                                : "Montant suggéré"}
                            </Button>
                          </div>
                        </div>
                      </div>
                      {serversFeatureEnabled && (
                        <div className="pos-server-selector">
                          <div className="pos-server-selector__heading">
                            <Text strong>Serveur (optionnel)</Text>
                            <Button
                              type="link"
                              size="small"
                              icon={<TeamOutlined />}
                              onClick={() => setServerManagerOpen(true)}
                            >
                              Gérer
                            </Button>
                          </div>
                          <div className="pos-server-selector__options">
                            <Button
                              size="small"
                              type={
                                selectedServerId === null
                                  ? "primary"
                                  : "default"
                              }
                              onClick={() => setSelectedServerId(null)}
                            >
                              Aucun
                            </Button>
                            {posServers.map((server) => (
                              <Button
                                key={server.id}
                                size="small"
                                type={
                                  selectedServerId === server.id
                                    ? "primary"
                                    : "default"
                                }
                                onClick={() => setSelectedServerId(server.id)}
                              >
                                {server.name}
                              </Button>
                            ))}
                            {posServers.length === 0 && (
                              <Text type="secondary">
                                Aucun serveur configuré.
                              </Text>
                            )}
                          </div>
                        </div>
                      )}
                    </section>

                    <div className="pos-checkout-action">
                      {canUsePos ? (
                        <Button
                          className="pos-checkout-action__button"
                          type="primary"
                          block
                          size="large"
                          loading={submitting}
                          disabled={!canSubmitSale}
                          onClick={handleSubmit}
                        >
                          {checkoutActionPresentation.label}
                        </Button>
                      ) : (
                        <Button
                          className="pos-checkout-action__button"
                          type="primary"
                          block
                          size="large"
                          disabled
                        >
                          Action non autorisée
                        </Button>
                      )}
                      {canUsePos && cart.length > 0 && !selectedCustomer && (
                        <div className="pos-checkout-action__hint">
                          <LockOutlined aria-hidden="true" />
                          Sélectionnez un client pour encaisser.
                        </div>
                      )}
                      {canUsePos &&
                        selectedCustomer &&
                        checkoutActionPresentation.hint && (
                          <div className="pos-checkout-action__hint pos-checkout-action__hint--warning">
                            <ExclamationCircleOutlined aria-hidden="true" />
                            {checkoutActionPresentation.hint}
                          </div>
                        )}
                    </div>
                  </div>
                </Card>
              </Col>
              )}
            </Row>
          )}
        </Content>
        {isMobile && (
          <Footer className="pos-mobile-footer">
            <div className="pos-mobile-footer__summary">
              <div>
                <span>{cartItemCount} articles</span>
                <strong>{total.toLocaleString()} CFA</strong>
              </div>
              <div className="pos-mobile-footer__hint">
                {selectedCustomer
                  ? "Client sélectionné"
                  : "Sélectionnez un client dans le panier"}
              </div>
            </div>
            <div className="pos-mobile-footer__actions">
              <Button
                className="pos-mobile-footer__button"
                icon={<MenuOutlined />}
                type={!showCart ? "primary" : "default"}
                onClick={() => setShowCart(false)}
              >
                Catalogue
              </Button>
              <Button
                className="pos-mobile-footer__button"
                icon={<ShoppingCartOutlined />}
                type={showCart ? "primary" : "default"}
                onClick={() => setShowCart(true)}
              >
                Panier ({cartItemCount})
              </Button>
            </div>
          </Footer>
        )}
      </Layout>

      {/* Composant de ticket de caisse */}
      <TicketCaisse
        visible={ticketVisible}
        order={orderData}
        onClose={handleTicketClose}
      />

      <RestaurantOrdersPosPanel
        open={restaurantOrdersPanelOpen}
        onClose={() => setRestaurantOrdersPanelOpen(false)}
        orders={activeRestaurantOrders}
        loading={restaurantOrdersLoading}
        error={restaurantOrdersError}
        selectedOrderId={selectedRestaurantOrderId}
        onSelectOrder={setSelectedRestaurantOrderId}
        onRefresh={() => fetchRestaurantOrders({ silent: false })}
        onAction={handleRestaurantOrderAction}
        onConvert={openConvertModal}
        convertDisabled={convertSubmitting}
        actionLoading={restaurantActionLoading}
        isMobile={isMobile}
      />

      {convertModalNode}

      <ServerManagerModal
        open={serverManagerOpen}
        onClose={() => setServerManagerOpen(false)}
        companyId={normalizedCompanyId}
        warehouseId={normalizedWarehouseId}
        onChanged={() => fetchPosServers()}
      />

      <PosReprintCenter
        open={reprintCenterOpen}
        onClose={() => setReprintCenterOpen(false)}
        companyId={normalizedCompanyId}
        warehouseId={normalizedWarehouseId}
        warehouseData={warehouseData}
        customers={customers}
        isMobile={isMobile}
        onOpenReceipt={handleOpenReprintReceipt}
      />

      {/* Modal de création rapide de client */}
      <QuickClientModal
        isOpen={quickClientModalOpen}
        onClose={() => setQuickClientModalOpen(false)}
        onSuccess={handleQuickClientSuccess}
        currentWarehouseId={selectedWarehouse}
      />

      <StockShortageActionsModal
        open={stockShortageItems.length > 0}
        title="Approvisionner les produits manquants pour la vente"
        items={stockShortageItems}
        canPurchase={canCreatePurchase}
        canAdjust={canCreateStockAdjustment}
        canProduce={canCreateProduction}
        productionTerm={productionTerminology.costContext}
        productionActionLabel={`${productionTerminology.launchAction} — recommandé`}
        onCancel={() => setStockShortageItems([])}
        onPurchase={() => navigateToStockResolution("purchase")}
        onAdjustment={() => navigateToStockResolution("adjustment")}
        onProduction={() => navigateToStockResolution("production")}
      />
    </Layout>
  );
};

export default GestionPos;
